import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import EmissionFilters from './emissions/EmissionFilters';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Plus, Filter, X, Search, Cloud, RotateCcw } from 'lucide-react';
import { ModulePageHeader } from '../components/ModulePageHeader';
import { LoadErrorState } from '../components/LoadErrorState';
import { toast } from 'sonner';
import EmissionEntryForm from '../components/EmissionEntryForm';
import EmissionEditForm from '../components/EmissionEditForm';
import { useCalcEngine } from '../hooks/useCalcEngine';
import { useEmissionsCoreData } from '../hooks/useEmissionsCoreData';
import { useEmissionsCalculator } from '../hooks/useEmissionsCalculator';
import { useGHGAccess } from '../hooks/useKPIAccess';
import {
  unitsMatch as unitsMatchShared,
  isVolumeUnit as isVolumeUnitShared,
  getConversionFactor as getConversionFactorShared,
  hasConversionDefined as hasConversionDefinedShared,
} from './emissions/utils/units';
import useEvidenceManagement from './emissions/useEvidenceManagement';
import { persistCalcAuditLog as persistCalcAuditLogShared } from './emissions/utils/persistCalcAuditLog';
import { buildCustomFuelCalculationPayload } from './emissions/utils/customFuelCalcAdapter';
import { editEmissionDispatch as editEmissionDispatchShared } from './emissions/utils/editEmissionDispatch';
import { getEmissionUpdateErrorMessage } from './emissions/utils/apiErrorMessage';
import { validateFileSize } from '../lib/uploadUtils';
import { MONTHS } from '../modules/ghg/emissions/shared/constants/emission-form-constants';
import { categoryRegistry } from '../modules/emissions';
import { formatEmissionQuantity, resolveEmissionQuantity } from '../modules/ghg/emissions/shared/utils/emissionQuantity';
import { selectClosestScope3Factors } from '../modules/ghg/emissions/shared/utils/scope3FactorYear';
import {
  deriveGhgFields,
  resolveGhgFormContext,
  resolveGhgFormArchitecture,
  resolveGhgCategoryOptions,
  resolveGhgScope3Options,
  resolveEffectiveScopeCode,
  GHG_FIELD_OPTION_KEYS,
} from '../modules/ghg/config';
import {
  createEmptyEmissionDraft,
  resetEmissionDraftForScope3MethodChange,
  resetEmissionDraftForSelectionChange,
  updateDraftField,
  updateDraftValues,
} from '../modules/ghg/emissions/shared/domain';
import {
  normalizeDensityForCalcEngine,
  normalizeCustomFuelCalorificValueUnit,
  normalizeCustomFuelCompoundUnit,
  normalizeCustomFuelDensityUnit,
  normalizeCustomFuelQuantityUnit,
  resolveCompoundDenominatorBasis,
  resolveProcessEfDenominatorBasis,
} from '../modules/ghg/emissions/shared/utils/unitHelpers';
import EmissionHistoryDialog from './emissions/components/EmissionHistoryDialog';
import EmissionDataGrid from './emissions/components/EmissionDataGrid';
import {
  filterSupplierVisibleScopes,
  filterSupplierVisibleCategories,
  resolveSupplierGhgOverrides,
} from '../modules/supplier-assessment/utils/supplierScopeAccess';
import { resolveSubmittedSupplierEmissionIds } from '../modules/supplier-assessment/utils/supplierGhgPeriodLocks';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const readPersistedSelectionValue = (value) => (
  value && typeof value === 'object' ? value.value || '' : value || ''
);

const C7_MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

const resolveC7ReportingMonth = (reportingPeriod) => {
  const value = String(reportingPeriod || '');
  const numericMatch = value.match(/^\d{4}-(\d{2})/);
  const numericIndex = numericMatch ? Number(numericMatch[1]) - 1 : -1;
  if (C7_MONTH_KEYS[numericIndex]) return C7_MONTH_KEYS[numericIndex];
  return C7_MONTH_KEYS.find((monthKey) => new RegExp(`\\b${monthKey}\\w*`, 'i').test(value)) || null;
};

const matchesOriginalEditSelection = ({
  emission,
  scope,
  category,
  biogenicScopeSelection,
  scope3Method,
  scope3ActivityType,
  scope3ActivityId,
}) => {
  if (!emission || emission.scope !== scope || emission.category !== category) return false;
  if (scope !== 'scope3') return true;
  const savedValues = emission.dynamic_field_values || {};
  const savedMethod = emission.calculation_method_scope3
    || readPersistedSelectionValue(savedValues.calculation_method_scope3);
  const savedActivityType = emission.scope3_activity_type
    || readPersistedSelectionValue(savedValues.scope3_activity_type);
  const savedActivityId = emission.scope3_ef_id
    || readPersistedSelectionValue(savedValues.scope3_ef_id);
  const savedBiogenicScope = emission.biogenic_scope_selection
    || readPersistedSelectionValue(savedValues.biogenic_scope_selection);

  return savedMethod === scope3Method
    && savedActivityType === scope3ActivityType
    && savedActivityId === scope3ActivityId
    && (scope !== 'biogenic' || savedBiogenicScope === biogenicScopeSelection);
};

export default function Emissions({ organizationGhgOverrides = null }) {
  // ============================================================================
  // CORE DATA - Fetched via custom hook (Phase 1 refactor)
  // ============================================================================
  const { getAuthHeader, user } = useAuth();
  const isSupplierUser = user?.user_type === 'supplier';
  const {
    emissions, facilities, organization, fuelDatabase,
    formulaDefinitions, formulaParameters, emissionConfigurations,
    loading, loadError: coreDataLoadError, centralizedUnits, gwpConfig,
    dynamicScopes, dynamicCategories, configLabels, organizationGhgOverrides: resolvedOrganizationGhgOverrides,
    scope3EFData: initialScope3EFData,
    fugitiveEmissionsData: initialFugitiveData,
    supplierReportingConfig,
    refresh: fetchData
  } = useEmissionsCoreData(getAuthHeader, { isSupplier: isSupplierUser });
  const effectiveOrganizationGhgOverrides = organizationGhgOverrides || resolvedOrganizationGhgOverrides;
  const effectiveSupplierGhgOverrides = useMemo(() => (
    isSupplierUser
      ? resolveSupplierGhgOverrides(effectiveOrganizationGhgOverrides, supplierReportingConfig)
      : effectiveOrganizationGhgOverrides
  ), [isSupplierUser, effectiveOrganizationGhgOverrides, supplierReportingConfig]);
  
  // ============================================================================
  // KPI ASSIGNMENT-BASED ACCESS CONTROL
  // ============================================================================
  const {
    accessInfo: kpiAccessInfo,
    loading: kpiAccessLoading,
    canAccessScope: kpiCanAccessScope,
    canAccessFacility: kpiCanAccessFacility,
    canAccessPeriod: kpiCanAccessPeriod,
    getPeriodRestrictions: kpiGetPeriodRestrictions,
    filterFacilitiesByScope,
    hasFullAccess: hasFullKPIAccess,
    allowedScopes: kpiAllowedScopes,
    periodRestrictions: kpiPeriodRestrictions,
  } = useGHGAccess();
  
  // ============================================================================
  // LOCAL UI STATE - Not fetched from API
  // ============================================================================
  const [isSaving, setIsSaving] = useState(false); // Track save operation state
  const [formulaDataReady, setFormulaDataReady] = useState(false); // Track when formula data is loaded
  const [dialogOpen, setDialogOpen] = useState(false);
  const [supplierSubmissionPeriods, setSupplierSubmissionPeriods] = useState([]);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedEmissionHistory, setSelectedEmissionHistory] = useState([]);
  // OCR Prefill Data - from AI Invoice Extractor workflow
  const [ocrPrefillData, setOcrPrefillData] = useState(null);
  // Drive the active scope from the route so /ghg/scope1, /ghg/scope2, etc.
  // each show only their own scope. Clicking a tab still works (in-page nav).
  const location = useLocation();
  const navigate = useNavigate();
  const pathScope = (location.pathname.match(/\/ghg\/(scope[123]|biogenic)/) || [])[1] || null;
  const [activeScope, setActiveScope] = useState(pathScope || 'scope1');
  const supplierEnabledScopes = useMemo(() => {
    if (!isSupplierUser) return null;
    const configured = supplierReportingConfig?.enabled_scopes || [];
    return ['scope1', 'scope2'].filter((scope) => configured.includes(scope));
  }, [isSupplierUser, supplierReportingConfig]);
  const visibleScopes = useMemo(() => (
    isSupplierUser
      ? filterSupplierVisibleScopes(dynamicScopes, supplierEnabledScopes)
      : dynamicScopes
  ), [dynamicScopes, isSupplierUser, supplierEnabledScopes]);
  
  // Handle OCR prefill from location state (when navigating from OCR Invoice page)
  useEffect(() => {
    if (location.state?.openAddForm && location.state?.ocrPrefill) {
      setOcrPrefillData(location.state.ocrPrefill);
      setDialogOpen(true);
      // Clear the state to prevent re-triggering on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);
  
  useEffect(() => {
    if (pathScope && pathScope !== activeScope) setActiveScope(pathScope);
  }, [pathScope]);
  useEffect(() => {
    if (!isSupplierUser || !supplierReportingConfig || !supplierEnabledScopes.length) return;
    if (!supplierEnabledScopes.includes(activeScope)) {
      const fallbackScope = supplierEnabledScopes[0];
      setActiveScope(fallbackScope);
      navigate(`/ghg/${fallbackScope}`, { replace: true });
    }
  }, [activeScope, isSupplierUser, navigate, supplierEnabledScopes, supplierReportingConfig]);
  const [filterFacility, setFilterFacility] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterFrequency, setFilterFrequency] = useState(''); // 'monthly', 'yearly', or '' for all
  const [filterCalculationMethod, setFilterCalculationMethod] = useState(''); // 'activity_basis', 'spend_basis', 'supplier_basis', or '' for all
  const [filterDateRange, setFilterDateRange] = useState({ from: null, to: null });
  const [searchQuery, setSearchQuery] = useState(''); // Search query for emissions
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('updated_at'); // Sort options: date, facility, fuel, emissions
  const [sortOrder, setSortOrder] = useState('desc'); // asc or desc
  const [editingEmission, setEditingEmission] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false); // Track calculation state for save button

  useEffect(() => {
    if (!isSupplierUser) {
      setSupplierSubmissionPeriods([]);
      return undefined;
    }
    let active = true;
    axios.get(`${API}/supplier-assessment/my-assessment/emissions/submission-periods`, { headers: getAuthHeader() })
      .then(({ data }) => { if (active) setSupplierSubmissionPeriods(data.periods || []); })
      .catch(() => { if (active) setSupplierSubmissionPeriods([]); });
    return () => { active = false; };
  }, [getAuthHeader, isSupplierUser]);

  const lockedSupplierEmissionIds = useMemo(() => (
    isSupplierUser
      ? resolveSubmittedSupplierEmissionIds(emissions, supplierSubmissionPeriods)
      : new Set()
  ), [emissions, isSupplierUser, supplierSubmissionPeriods]);
  const canAddSupplierEmission = !isSupplierUser
    || supplierSubmissionPeriods.length === 0
    || supplierSubmissionPeriods.some((period) => period.status !== 'submitted');
  
  // Modal Protection State (#19 - Prevent accidental close)
  const [isFormDirty, setIsFormDirty] = useState(false); // Track if form has unsaved changes
  const [hasChangedCalculationSelection, setHasChangedCalculationSelection] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false); // Confirmation dialog
  const [pendingCloseAction, setPendingCloseAction] = useState(null); // Store pending close action
  
  // Shared edit-domain state. It deliberately excludes dialog/loading/search
  // state so the renderer and page can exchange actual emission values only.
  const [editDraft, setEditDraft] = useState(() => createEmptyEmissionDraft());
  const formData = editDraft.values;
  const setFormData = useCallback((nextValuesOrUpdater) => {
    setEditDraft((currentDraft) => updateDraftValues(currentDraft, nextValuesOrUpdater));
  }, []);
  const setDraftField = useCallback((field, nextValueOrUpdater) => {
    setEditDraft((currentDraft) => updateDraftField(currentDraft, field, nextValueOrUpdater));
  }, []);
  const selectedCategory = editDraft.selectedCategory;
  const setSelectedCategory = useCallback((value) => setDraftField('selectedCategory', value), [setDraftField]);
  const overrideCalorificValue = editDraft.overrideCalorificValue;
  const setOverrideCalorificValue = useCallback((value) => setDraftField('overrideCalorificValue', value), [setDraftField]);
  const overrideDensity = editDraft.overrideDensity;
  const setOverrideDensity = useCallback((value) => setDraftField('overrideDensity', value), [setDraftField]);
  const overrideEmissionFactorHeat = editDraft.overrideEmissionFactorHeat;
  const overrideJustification = editDraft.overrideJustification;
  const setOverrideJustification = useCallback((value) => setDraftField('overrideJustification', value), [setDraftField]);
  
  // Scope 3 specific state for inline edit form
  // Initial data hydrated from useEmissionsCoreData (so dropdowns work for Edit
  // dialog regardless of whether user has touched the Add form / scope tab).
  const [scope3EFData, setScope3EFData] = useState([]);
  const emissionDataGridRef = useRef(null);
  const [hasCustomizedColumnWidths, setHasCustomizedColumnWidths] = useState(false);
  const scope3Method = editDraft.scope3Method;
  const setScope3Method = useCallback((value) => setDraftField('scope3Method', value), [setDraftField]);
  const spendCurrencyConversionMethod = editDraft.spendCurrencyConversionMethod;
  const scope3ActivityId = editDraft.scope3ActivityId;
  const setScope3ActivityId = useCallback((value) => setDraftField('scope3ActivityId', value), [setDraftField]);
  const scope3ActivityType = editDraft.scope3ActivityType;
  const setScope3ActivityType = useCallback((value) => setDraftField('scope3ActivityType', value), [setDraftField]);
  const scope3Subcategory = editDraft.scope3Subcategory;
  const setScope3Subcategory = useCallback((value) => setDraftField('scope3Subcategory', value), [setDraftField]);
  const typeOfProduct = editDraft.typeOfProduct;
  const setTypeOfProduct = useCallback((value) => setDraftField('typeOfProduct', value), [setDraftField]);
  const editCalcMethodology = editDraft.calculationMethodology;
  const setEditCalcMethodology = useCallback((value) => setDraftField('calculationMethodology', value), [setDraftField]);
  const editProcessType = editDraft.processType;
  const setEditProcessType = useCallback((value) => setDraftField('processType', value), [setDraftField]);
  const scope3CustomActivity = editDraft.scope3CustomActivity;
  const setScope3CustomActivity = useCallback((value) => setDraftField('scope3CustomActivity', value), [setDraftField]);
  const useCustomActivity = editDraft.useCustomActivity;
  const setUseCustomActivity = useCallback((value) => setDraftField('useCustomActivity', value), [setDraftField]);
  const editUseCustomFuel = editDraft.useCustomFuel;
  const setEditUseCustomFuel = useCallback((value) => setDraftField('useCustomFuel', value), [setDraftField]);
  const editCustomFuelName = editDraft.customFuelName;
  const setEditCustomFuelName = useCallback((value) => setDraftField('customFuelName', value), [setDraftField]);
  const [fugitiveEmissionsData, setFugitiveEmissionsData] = useState([]); // Fugitive emissions from fuel_database
  const [loadingScope3EF, setLoadingScope3EF] = useState(false);
  const [activitySearchTerm, setActivitySearchTerm] = useState(''); // Search filter for activities in edit dialog
  
  // Biogenic-specific state
  const biogenicScopeSelection = editDraft.biogenicScopeSelection;
  const setBiogenicScopeSelection = useCallback((value) => setDraftField('biogenicScopeSelection', value), [setDraftField]);
  const [biogenicCategories, setBiogenicCategories] = useState([]); // Categories that have biogenic entries
  const [loadingBiogenicCategories, setLoadingBiogenicCategories] = useState(false);
  
  // Multi-Employee state (for C7 Employee Commuting edit)
  // C7 always uses multi-employee mode - no toggle needed
  const editEmployees = editDraft.employees;
  const setEditEmployees = useCallback((value) => setDraftField('employees', value), [setDraftField]);
  const editEmployeeMonthlyTotals = editDraft.employeeMonthlyTotals;
  const setEditEmployeeMonthlyTotals = useCallback((value) => setDraftField('employeeMonthlyTotals', value), [setDraftField]);
  const editEmployeeYearlyTotal = editDraft.employeeYearlyTotal;
  const setEditEmployeeYearlyTotal = useCallback((value) => setDraftField('employeeYearlyTotal', value), [setDraftField]);
  const [isCalculatingEditEmployee, setIsCalculatingEditEmployee] = useState(false);
  const editC7Month = editDraft.c7Month;
  const setEditC7Month = useCallback((value) => setDraftField('c7Month', value), [setDraftField]);
  
  // Frequency type state for edit mode (monthly vs yearly)
  // This is locked once a record is saved and cannot be changed
  const editFrequencyType = editDraft.frequencyType;
  const setEditFrequencyType = useCallback((value) => setDraftField('frequencyType', value), [setDraftField]);
  
  // Backend calc engine hook
  const { 
    executeCalculation: executeBackendCalc, 
    isCalculating: isBackendCalculating, 
    error: calcEngineError 
  } = useCalcEngine(getAuthHeader);
  
  // ============================================================================
  // DYNAMIC FORM CONFIG - Loaded from ce_input_field_mappings via form-config API
  // This replaces hardcoded fields with database-driven configuration
  // ============================================================================
  const [editFormConfig, setEditFormConfig] = useState(null);
  const [editFormConfigLoading, setEditFormConfigLoading] = useState(false);
  
  // Loading state for edit dialog - prevents showing partial/stale data
  const [isEditLoading, setIsEditLoading] = useState(false);
  
  // Dynamic input field values for the edit form (keyed by field.field_key)
  const dynamicFieldValues = editDraft.dynamicFieldValues;
  const setDynamicFieldValues = useCallback((value) => setDraftField('dynamicFieldValues', value), [setDraftField]);
  
  // Store the emission ID being edited (for fetching audit log)
  const [editingEmissionId, setEditingEmissionId] = useState(null);
  
  // Store the fetched audit log for the emission
  const [emissionAuditLog, setEmissionAuditLog] = useState([]);

  // New: Monthly data structure for year-based entry
  const [reportingYear, setReportingYear] = useState(new Date().getFullYear().toString());
  const [monthlyData, setMonthlyData] = useState({});
  const [formStep, setFormStep] = useState(1); // Step-based form


  // Synchronously identifies the latest record-opening request so delayed
  // evidence metadata cannot update a newer draft.
  const activeEditIdRef = useRef(null);

  const [uploadedEvidence, setUploadedEvidence] = useState(null);
  const existingEvidences = editDraft.existingEvidences;
  const setExistingEvidences = useCallback((value) => setDraftField('existingEvidences', value), [setDraftField]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [emissionToDelete, setEmissionToDelete] = useState(null);

  // Helper functions for centralized labels
  const getMethodLabel = useCallback((method, short = false) => {
    if (!method) return '-';
    const labels = short ? configLabels.calculation_methods_short : configLabels.calculation_methods;
    return labels?.[method] || method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }, [configLabels]);

  // Mark formula data as ready when core data loads
  useEffect(() => {
    if (!loading && formulaDefinitions.length >= 0) {
      setFormulaDataReady(true);
    }
  }, [loading, formulaDefinitions]);

  // Hydrate Scope 3 EF + fugitive emissions data from useEmissionsCoreData on
  // mount. Previously this fetch was gated by `formData.scope === 'scope3'`,
  // which left the Edit dialog with an empty dataset (only `supplier_basis`
  // visible) when the user opened it before touching the Add form.
  useEffect(() => {
    if (Array.isArray(initialScope3EFData) && initialScope3EFData.length) {
      setScope3EFData(initialScope3EFData);
    }
  }, [initialScope3EFData]);

  useEffect(() => {
    if (Array.isArray(initialFugitiveData) && initialFugitiveData.length) {
      setFugitiveEmissionsData(initialFugitiveData);
    }
  }, [initialFugitiveData]);

  // Fetch biogenic categories when biogenic tab is active and scope3 is selected
  useEffect(() => {
    const fetchBiogenicCategories = async () => {
      if (activeScope !== 'biogenic' || biogenicScopeSelection !== 'scope3') {
        return;
      }
      
      setLoadingBiogenicCategories(true);
      try {
        const response = await axios.get(`${API}/scope3-ef/categories-by-sub-scope?sub_scope=biogenic`, {
          headers: getAuthHeader()
        });
        setBiogenicCategories(response.data?.categories || []);
      } catch (error) {
        console.error('[Biogenic] Error fetching categories:', error);
        setBiogenicCategories([]);
      } finally {
        setLoadingBiogenicCategories(false);
      }
    };
    
    fetchBiogenicCategories();
  }, [activeScope, biogenicScopeSelection, getAuthHeader]);

  // Fetch biogenic scope3_ef data when biogenic tab + scope3 + category is selected
  useEffect(() => {
    const fetchBiogenicScope3EF = async () => {
      if (activeScope !== 'biogenic' || biogenicScopeSelection !== 'scope3') {
        return;
      }
      
      setLoadingScope3EF(true);
      try {
        // Fetch scope3_ef with sub_scope=biogenic filter
        const response = await axios.get(`${API}/scope3-ef?sub_scope=biogenic&limit=10000`, {
          headers: getAuthHeader()
        });
        const efData = response.data?.data || response.data || [];
        setScope3EFData(Array.isArray(efData) ? efData : []);
      } catch (error) {
        console.error('[Biogenic Scope3 EF] Error fetching:', error);
        setScope3EFData([]);
      } finally {
        setLoadingScope3EF(false);
      }
    };
    
    fetchBiogenicScope3EF();
  }, [activeScope, biogenicScopeSelection, getAuthHeader]);

  // ============================================================================
  // FETCH FORM CONFIG when category+scope changes in edit dialog
  // This loads dynamic input field mappings from the backend
  // ============================================================================
  useEffect(() => {
    let cancelled = false;
    const fetchFormConfig = async () => {
      if (!dialogOpen || !formData.category || !formData.scope) {
        setEditFormConfig(null);
        setEditFormConfigLoading(false);
        return;
      }
      
      const editContext = resolveGhgFormContext({
        scope: formData.scope,
        biogenicScopeSelection,
        categoryName: formData.category,
        categories: dynamicCategories,
        scopes: dynamicScopes,
      });
      if (!editContext.categoryId) {
        setEditFormConfig(null);
        setEditFormConfigLoading(false);
        return;
      }
      
      setEditFormConfigLoading(true);
      try {
        // Include method and activity in the request for better formula matching
        const configParams = new URLSearchParams({ scope: editContext.effectiveScope });
        let url = `${API}/calc-engine/form-config/${editContext.categoryId}?${configParams.toString()}`;
        if (scope3Method) url += `&method=${scope3Method}`;
        if (scope3ActivityType) url += `&activity_type=${scope3ActivityType}`;
        
        const response = await axios.get(url, { headers: getAuthHeader() });
        if (!cancelled) setEditFormConfig(response.data);
      } catch (err) {
        if (!cancelled) {
          console.error('[EDIT FORM CONFIG] Failed to fetch form config:', err);
          setEditFormConfig(null);
        }
      } finally {
        if (!cancelled) setEditFormConfigLoading(false);
      }
    };
    
    fetchFormConfig();
    return () => {
      cancelled = true;
    };
  }, [dialogOpen, formData.category, formData.scope, dynamicCategories, dynamicScopes, getAuthHeader, biogenicScopeSelection, scope3Method, scope3ActivityType, scope3ActivityId, editingEmission, hasChangedCalculationSelection]);
  
  // ============================================================================
  // EDIT FIELD DERIVATION
  // Existing-record hydration remains below; this is the same resolved-config
  // and explicit-context derivation pipeline used by Create.
  // ============================================================================
  const editSelectedFuel = useMemo(
    () => fuelDatabase.find((fuel) => fuel.id === formData.fuel_id) || null,
    [fuelDatabase, formData.fuel_id],
  );

  const editDecisionFieldValues = useMemo(
    () => ({
      calculation_methodology: editCalcMethodology || 'using_heat_basis_ncv',
      ...(editProcessType ? { process_type: editProcessType } : {}),
      ...(editDraft.allocationMethod ? { allocation_method: editDraft.allocationMethod } : {}),
    }),
    [editCalcMethodology, editProcessType, editDraft.allocationMethod],
  );

  const isOriginalEditSelection = useMemo(() => !hasChangedCalculationSelection && matchesOriginalEditSelection({
    emission: editingEmission,
    scope: formData.scope,
    category: formData.category,
    biogenicScopeSelection,
    scope3Method,
    scope3ActivityType,
    scope3ActivityId,
  }), [
    editingEmission,
    formData.scope,
    formData.category,
    biogenicScopeSelection,
    scope3Method,
    scope3ActivityType,
    scope3ActivityId,
    hasChangedCalculationSelection,
  ]);

  const editGhgFormContext = useMemo(
    () =>
      resolveGhgFormContext({
        scope: formData.scope,
        biogenicScopeSelection,
        categoryName: formData.category || selectedCategory,
        // Records converted to C7 before canonical category metadata was
        // persisted can still carry the previous category_code. Prefer C7's
        // canonical identity when the saved display category is unambiguously
        // Employee Commuting so those records reopen in the employee-row form.
        categoryCode: isOriginalEditSelection
          ? (/^c7\b|employee commuting/i.test(formData.category || selectedCategory || '')
              ? 'employee_commuting'
              : editingEmission?.category_code || null)
          : null,
        categories: dynamicCategories,
        scopes: dynamicScopes,
        scope3Method,
        spendCurrencyConversionMethod,
        allocationMethod: editDraft.allocationMethod,
        scope3ActivityType,
        scope3Subcategory,
        typeOfProduct,
        decisionFieldValues: editDecisionFieldValues,
        useCustomFuel: editUseCustomFuel,
        selectedFuel: editSelectedFuel,
        savedFormulaId: isOriginalEditSelection ? editingEmission?.formula_id || null : null,
      }),
    [
      formData.scope,
      formData.category,
      selectedCategory,
      editingEmission?.category_code,
      biogenicScopeSelection,
      dynamicCategories,
      dynamicScopes,
      scope3Method,
      spendCurrencyConversionMethod,
      editDraft.allocationMethod,
      scope3ActivityType,
      scope3Subcategory,
      typeOfProduct,
      editDecisionFieldValues,
      editUseCustomFuel,
      editSelectedFuel,
      editingEmission?.formula_id,
      isOriginalEditSelection,
    ],
  );

  const editGhgFormArchitecture = useMemo(
    () => resolveGhgFormArchitecture({
      standardConfig: editFormConfig,
      organizationOverrides: effectiveSupplierGhgOverrides,
      formContext: editGhgFormContext,
      biogenicScopeSelection,
    }),
    [editFormConfig, effectiveSupplierGhgOverrides, editGhgFormContext, biogenicScopeSelection],
  );
  const editCapabilities = editGhgFormArchitecture.capabilities;
  const editGhgFieldOptions = editGhgFormArchitecture.resolvedFieldOptions;
  const resolvedEditGhgConfig = editGhgFormArchitecture.resolvedConfig;

  const dynamicInputFieldsResult = useMemo(
    () => deriveGhgFields({ formConfig: resolvedEditGhgConfig, context: editGhgFormContext }),
    [resolvedEditGhgConfig, editGhgFormContext],
  );

  const dynamicInputFields = dynamicInputFieldsResult.fields;

  // Older Process Emissions records did not persist process_type. Once the
  // form config arrives, recover it from the saved formula's decision-tree
  // branch so the edit form can resolve its inputs and live calculation.
  useEffect(() => {
    const isProcessEmission = editGhgFormContext.isProcessCategory;
    if (!dialogOpen || !isProcessEmission || editProcessType || !editingEmission?.formula_id || !editFormConfig?.decision_tree) {
      return;
    }

    const findProcessTypeForFormula = (node) => {
      if (!node || typeof node !== 'object') return '';
      const options = node.options || {};
      for (const [optionValue, option] of Object.entries(options)) {
        if (option?.formula_id === editingEmission.formula_id && node.field_name === 'process_type') {
          return optionValue;
        }
        const nestedMatch = findProcessTypeForFormula(option?.next);
        if (nestedMatch) {
          return node.field_name === 'process_type' ? optionValue : nestedMatch;
        }
      }
      return '';
    };

    const inferredProcessType = findProcessTypeForFormula(editFormConfig.decision_tree);
    if (inferredProcessType) {
      setEditProcessType(inferredProcessType);
    }
  }, [dialogOpen, editGhgFormContext.isProcessCategory, editProcessType, editingEmission?.formula_id, editFormConfig]);

  // Build decision context from dynamic field values
  const buildEditDecisionInputs = useCallback(() => {
    const decisionInputs = {};
    
    // Fields that should NOT be in decision_inputs (they are formula properties/overrides, not decision tree inputs)
    const excludeFromDecisionInputs = ['ppp', 'inflation_rate', 'cv', 'density', 'co2_gwp_fugitives'];
    
    dynamicInputFields.forEach(field => {
      if (field.mapsToContext && !excludeFromDecisionInputs.includes(field.mapsToContext)) {
        const value = dynamicFieldValues[field.variable];
        const hasValue = value !== undefined && value !== null && value !== '';
        
        // For optional non-override fields (like ef_quantity), check if checkbox is enabled
        // This ensures unchecking the checkbox sets mapsToContext to "false" even if value exists
        const isOptionalField = !field.required && !field.isOverride;
        const isCheckboxEnabled = isOptionalField 
          ? (dynamicFieldValues[`override_${field.variable}`] || false)
          : true; // Non-optional fields don't have checkboxes, always "enabled"
        
        // Field is considered "active" only if it has a value AND (for optional fields) checkbox is enabled
        const isFieldActive = hasValue && isCheckboxEnabled;
        
        decisionInputs[field.mapsToContext] = isFieldActive 
          ? field.mapsToContextValueWhenFilled 
          : field.mapsToContextValueWhenEmpty;
      }
    });
    
    // Determine if this is a scope3-like flow
    const isBiogenicScope3 = formData.scope === 'biogenic' && biogenicScopeSelection === 'scope3';
    const isScope3Like = formData.scope === 'scope3' || isBiogenicScope3;
    
    // For Scope 3 (or biogenic scope3), add calculation_method_scope3 from the selected method
    if (isScope3Like && scope3Method) {
      decisionInputs['calculation_method_scope3'] = scope3Method;
      if (editDraft.allocationMethod) {
        decisionInputs['allocation_method'] = editDraft.allocationMethod;
      }
      if (scope3Method === 'spend_basis') {
        decisionInputs['spend_currency_conversion_method'] = spendCurrencyConversionMethod || 'standard';
      }
    }
    
    // For Scope 3 with activity_type (C6/C7), add activity_type to decision inputs
    if (isScope3Like && scope3ActivityType) {
      decisionInputs['activity_type'] = scope3ActivityType;
    }
    
    // For Scope 3 with subcategory (C8/C10/C11/C13/C14), add subcategory_selection to decision inputs
    if (isScope3Like && scope3Subcategory) {
      decisionInputs['subcategory_selection'] = scope3Subcategory;
    }

    // C11 only — type_of_product is a downstream decision-tree node.
    if (isScope3Like && typeOfProduct) {
      decisionInputs['type_of_product'] = typeOfProduct;
    }
    
    // For biogenic scope3 with subcategory categories (C8/C10/C11/C13/C14),
    // pass 'biogenic' as subcategory_selection to satisfy the decision tree
    // (biogenic skips subcategory UI but backend decision tree still expects it)
    if (isBiogenicScope3 && !decisionInputs['subcategory_selection']) {
      const catLower = (formData.category || selectedCategory)?.toLowerCase() || '';
      const isSubcategoryCategory = editCapabilities.subcategory;
      if (isSubcategoryCategory) {
        decisionInputs['subcategory_selection'] = 'biogenic';
      }
    }
    
    // For Scope 1 / Biogenic Scope 1: add calculation_methodology and process_type
    const isBiogenicScope1 = formData.scope === 'biogenic' && biogenicScopeSelection === 'scope1';
    if (formData.scope === 'scope1' || isBiogenicScope1) {
      decisionInputs['calculation_methodology'] = editCalcMethodology || 'using_heat_basis_ncv';
      if (editProcessType) {
        decisionInputs['process_type'] = editProcessType;
      }

      if (decisionInputs.calculation_methodology === 'using_qty_basis_ef') {
        if (editGhgFormContext.isStationaryMobileOrFlaringCategory
          || editGhgFormContext.categoryCode === 'process_emissions'
          || editUseCustomFuel) {
          const efField = dynamicInputFields.find((field) => (
            field.variable === 'ef_quantity' || field.fieldKey === 'ef_quantity'
          ));
          const efUnit = editUseCustomFuel
            ? dynamicFieldValues.custom_ef_unit || 'kgCO2/kg'
            : dynamicFieldValues.ef_quantity_unit
            || dynamicFieldValues.ef_quantity?.unit
            || editSelectedFuel?.emission_factor_basis_unit
            || efField?.defaultUnit
            || efField?.default_unit
            || efField?.expectedUnit
            || efField?.allowedUnits?.[0];
          const basis = resolveProcessEfDenominatorBasis(efUnit, centralizedUnits);
          if (basis) decisionInputs.ef_quantity_basis = basis;
        }
      }

      if (decisionInputs.calculation_methodology === 'using_heat_basis_ncv') {
        const cvField = dynamicInputFields.find((field) => (
          field.variable === 'cv' || field.fieldKey === 'cv'
        ));
        const cvUnit = (editUseCustomFuel ? dynamicFieldValues.custom_cv_unit : null)
          || dynamicFieldValues.cv_unit
          || dynamicFieldValues.cv?.unit
          || editSelectedFuel?.calorific_value_unit
          || cvField?.defaultUnit
          || cvField?.default_unit
          || cvField?.expectedUnit
          || cvField?.allowedUnits?.[0]
          || 'TJ/kg';
        const basis = resolveCompoundDenominatorBasis(cvUnit, centralizedUnits);
        if (basis) decisionInputs.cv_quantity_basis = basis;
      }
    }
    
    return decisionInputs;
  }, [dynamicInputFields, dynamicFieldValues, formData.scope, scope3Method, spendCurrencyConversionMethod, scope3ActivityType, scope3Subcategory, typeOfProduct, biogenicScopeSelection, selectedCategory, editCalcMethodology, editProcessType, editCapabilities, editGhgFormContext.categoryCode, centralizedUnits, editUseCustomFuel, editDraft.allocationMethod, editSelectedFuel]);

  // Helper to update dynamic field values
  const updateDynamicFieldValue = useCallback((key, value) => {
    setDynamicFieldValues(prev => ({ ...prev, [key]: value }));
    setIsFormDirty(true); // Mark form as dirty when dynamic fields change
  }, []);

  // ============================================================================
  // FETCH AUDIT LOG AND POPULATE DYNAMIC FIELDS
  // When editing, fetch the audit log from ce_calculation_audit_logs table
  // and populate dynamic field values based on what was actually used
  // ============================================================================
  useEffect(() => {
    const populateDynamicFields = async () => {
      // Skip if not editing or no emission ID
      if (!dialogOpen || !editingEmissionId || !editingEmission) {
        return;
      }
      
      // Wait for dynamicInputFields to be loaded
      if (dynamicInputFields.length === 0) {
        return;
      }

      // Do not rehydrate the original record's inputs after the user changes
      // scope, category, method, or activity in the current edit draft.
      if (!isOriginalEditSelection) {
        return;
      }
      
      // PRIMARY: Read from emission.dynamic_field_values (new structure)
      const savedDynamicValues = editingEmission.dynamic_field_values || {};
      
      // Hydrate calculation methodology from its explicit saved value first.
      // Legacy records fall back to inferring the method from their saved fields.
      const savedMethod = editingEmission.calculation_methodology
        || savedDynamicValues.calculation_methodology;
      const savedCalculationMethodology = typeof savedMethod === 'object'
        ? savedMethod?.value
        : savedMethod;
      const dfvKeys = Object.keys(savedDynamicValues);
      if (savedCalculationMethodology) {
        setEditCalcMethodology(savedCalculationMethodology);
      } else if (dfvKeys.includes('carbon_content') || dfvKeys.includes('composition_of_carbon') || dfvKeys.includes('custom_carbon_content')) {
        setEditCalcMethodology('using_carbon_composition');
      } else if ((dfvKeys.includes('ef_quantity') && savedDynamicValues['ef_quantity']?.value !== null) || (dfvKeys.includes('custom_ef') && !dfvKeys.includes('custom_cv'))) {
        setEditCalcMethodology('using_qty_basis_ef');
      } else {
        setEditCalcMethodology('using_heat_basis_ncv');
      }
      
      // Hydrate process type from saved fields
      const savedProcessType = savedDynamicValues.process_type;
      if (savedProcessType) {
        setEditProcessType(savedProcessType.value || savedProcessType || '');
      } else {
        setEditProcessType('');
      }
      
      // Helper to get the correct unit for a field
      // When editing, we primarily use the saved unit from dynamic_field_values
      // The fallback is only needed for fields without saved units
      const getFieldUnit = (field, savedUnit) => {
        if (savedUnit) return savedUnit;
        // If unitSource is 'fuel', use fuel's allowed_units
        const selectedFuelForUnit = fuelDatabase.find(f => f.id === formData.fuel_id);
        if (field.unitSource === 'fuel' && selectedFuelForUnit?.allowed_units?.length > 0) {
          return selectedFuelForUnit.allowed_units[0];
        }
        // Fallback: use field's allowed units or expected unit
        const fieldUnits = field.allowedUnits?.length > 0 ? field.allowedUnits : [field.expectedUnit].filter(Boolean);
        return fieldUnits[0] || field.expectedUnit || '';
      };
      
      if (Object.keys(savedDynamicValues).length > 0) {
        // New structure - populate directly from saved dynamic_field_values
        const values = {};
        
        // Map legacy/alternate field names to formula variable names
        // This handles backwards compatibility for older bulk upload records
        const legacyFieldMap = {
          // Legacy spend_basis records might have used 'spend_amount' instead of 'spent_value'
          'spend_amount': 'spent_value',
          // Legacy transport records might have used these names
          'distance_travelled': 'km_travelled',
          'quantity_of_goods': 'qty_travelled',
          'passengers': 'qty_passenger',
          'number_of_rooms': 'qty_room',
          'number_of_nights': 'qty_nights',
          'working_hours': 'working_hour_per_day',
        };
        
        dynamicInputFields.forEach(field => {
          const variable = field.variable;
          let savedField = savedDynamicValues[variable];
          
          // If field not found, check if there's a legacy field name
          if (!savedField) {
            // Check if any legacy key maps to this variable
            for (const [legacyKey, currentVar] of Object.entries(legacyFieldMap)) {
              if (currentVar === variable && savedDynamicValues[legacyKey]) {
                savedField = savedDynamicValues[legacyKey];
                break;
              }
            }
          }
          
          if (savedField) {
            values[variable] = savedField.value !== null && savedField.value !== undefined 
              ? savedField.value.toString() 
              : '';
            // Always use the saved unit if it exists - it will be added to dropdown options if needed
            values[`${variable}_unit`] = savedField.unit || getFieldUnit(field, null);
            
            // For override fields OR optional fields (not required, not override), restore checkbox state
            const isOptionalField = !field.required && !field.isOverride;
            if (field.isOverride || isOptionalField) {
              // Check is_override flag, or fallback to checking if value exists for override fields
              // This handles both new records (with is_override flag) and older records (without flag)
              const isOverrideActive = savedField.is_override === true || 
                (savedField.value !== null && savedField.value !== undefined && savedField.value !== '' && savedField.value !== 0);
              values[`override_${variable}`] = isOverrideActive;
              if (field.isOverride) {
                values[`${variable}_justification`] = savedField.justification || '';
              }
            }
          } else {
            // Field not in saved data - initialize with correct unit
            values[variable] = '';
            values[`${variable}_unit`] = getFieldUnit(field, null);
            // For override fields OR optional fields, initialize checkbox to false
            const isOptionalField = !field.required && !field.isOverride;
            if (field.isOverride || isOptionalField) {
              values[`override_${variable}`] = false;
            }
          }
        });

        // Process Emissions can render Density as a virtual runtime field.
        // Hydrate it even when its configuration does not include a Density mapping.
        if (!dynamicInputFields.some((field) => field.variable === 'density') && savedDynamicValues.density) {
          values.density = savedDynamicValues.density.value !== null && savedDynamicValues.density.value !== undefined
            ? savedDynamicValues.density.value.toString()
            : '';
          values.density_unit = savedDynamicValues.density.unit || '';
          values.override_density = savedDynamicValues.density.is_override === true;
        }
        
        setDynamicFieldValues(values);

        // Hydrate custom fuel per-month fields from saved dynamic_field_values
        if (editingEmission.is_custom_fuel || editingEmission.is_custom_factor) {
          const customKeyMap = {
            custom_ef: ['custom_ef', 'ef_quantity', 'ef', 'emission_factor'],
            custom_cv: ['custom_cv', 'cv', 'ncv', 'calorific_value'],
            custom_carbon_content: ['custom_carbon_content', 'carbon_content', 'composition_of_carbon'],
            custom_oxidation_factor: ['custom_oxidation_factor', 'oxidation_factor'],
          };
          Object.entries(customKeyMap).forEach(([key, aliases]) => {
            const saved = aliases.map(alias => savedDynamicValues[alias]).find(Boolean);
            if (saved) {
              values[key] = saved.value !== null && saved.value !== undefined ? saved.value.toString() : '';
              if (saved.unit) {
                values[`${key}_unit`] = key === 'custom_cv'
                  ? normalizeCustomFuelCalorificValueUnit(saved.unit)
                  : normalizeCustomFuelCompoundUnit(saved.unit);
              }
            }
          });
          // Custom fuel quantity unit is persisted on dynamic_field_values.qty.
          // Top-level fields are retained only for records saved before that contract.
          const savedQty = savedDynamicValues.qty;
          const savedQtyUnit = typeof savedQty === 'object' ? savedQty.unit : '';
          const primaryQuantity = resolveEmissionQuantity(editingEmission);
          values.custom_qty_unit = normalizeCustomFuelQuantityUnit(savedQtyUnit
            || primaryQuantity.unit
            || 'kg');
          // Density from dynamic_field_values
          if (savedDynamicValues.density) {
            values.density = savedDynamicValues.density.value?.toString() || '';
            values.density_unit = normalizeCustomFuelDensityUnit(
              savedDynamicValues.density.unit || 'kg/L',
            );
          }
          setDynamicFieldValues({ ...values });
        }
        
        // Also try to fetch audit log for display purposes
        try {
          const response = await axios.get(
            `${API}/user/calc-engine/audit-log/${editingEmissionId}`,
            { headers: getAuthHeader() }
          );
          setEmissionAuditLog(response.data?.audit_log || []);
        } catch (error) {
          console.warn('Could not fetch audit log:', error);
          setEmissionAuditLog([]);
        }
        
        return;
      }
      
      // FALLBACK: Try to populate from audit log (for records created before this change)
      try {
        const response = await axios.get(
          `${API}/user/calc-engine/audit-log/${editingEmissionId}`,
          { headers: getAuthHeader() }
        );
        
        const auditLog = response.data?.audit_log || [];
        setEmissionAuditLog(auditLog);
        
        if (!response.data?.found || auditLog.length === 0) {
          // No audit log - initialize fields with correct units
          const emptyValues = {};
          dynamicInputFields.forEach(field => {
            emptyValues[field.variable] = '';
            emptyValues[`${field.variable}_unit`] = getFieldUnit(field, null);
            // For override fields OR optional fields, initialize checkbox to false
            const isOptionalField = !field.required && !field.isOverride;
            if (field.isOverride || isOptionalField) {
              emptyValues[`override_${field.variable}`] = false;
            }
          });
          setDynamicFieldValues(emptyValues);
          return;
        }
        
        // Populate from audit log - ALL fields including DB-sourced values
        const inputMap = {};
        const propertyMap = {};
        const contextMap = {};
        
        auditLog.forEach(e => {
          if (e.step === 'input') {
            inputMap[e.variable] = e;
          }
          if (e.step === 'resolve_property') {
            propertyMap[e.property] = e;
          }
        });
        
        // Also check the context from the response
        const context = response.data?.context || {};
        Object.entries(context).forEach(([key, val]) => {
          if (typeof val === 'object' && val !== null && 'value' in val) {
            contextMap[key] = val;
          }
        });
        
        const values = {};
        
        dynamicInputFields.forEach(field => {
          const variable = field.variable;
          
          const inputEntry = inputMap[variable];
          const propertyEntry = propertyMap[variable];
          const contextEntry = contextMap[variable];
          
          if (inputEntry) {
            // User input - always populate
            values[variable] = inputEntry.value?.toString() || '';
            values[`${variable}_unit`] = getFieldUnit(field, inputEntry.unit);
          } else if (propertyEntry) {
            // Property resolved during calculation
            if (propertyEntry.source === 'user_override') {
              // User overrode this property
              values[variable] = propertyEntry.value?.toString() || '';
              values[`${variable}_unit`] = getFieldUnit(field, propertyEntry.unit);
              values[`override_${variable}`] = true;
            } else if (field.isOverride) {
              // DB-sourced property for an override field - show value but don't check override
              values[variable] = propertyEntry.value?.toString() || '';
              values[`${variable}_unit`] = getFieldUnit(field, propertyEntry.unit);
              values[`override_${variable}`] = false;
            } else if (!field.required) {
              // Optional field with DB value
              values[variable] = propertyEntry.value?.toString() || '';
              values[`${variable}_unit`] = getFieldUnit(field, propertyEntry.unit);
              values[`override_${variable}`] = false;
            } else {
              // Regular required field with DB value
              values[variable] = propertyEntry.value?.toString() || '';
              values[`${variable}_unit`] = getFieldUnit(field, propertyEntry.unit);
            }
          } else if (contextEntry) {
            // Value from context (execution result)
            values[variable] = contextEntry.value?.toString() || '';
            values[`${variable}_unit`] = getFieldUnit(field, contextEntry.unit);
            // For override fields OR optional fields, initialize checkbox to false
            const isOptionalField = !field.required && !field.isOverride;
            if (field.isOverride || isOptionalField) {
              values[`override_${variable}`] = false;
            }
          } else {
            // Field not found - initialize with correct unit
            values[variable] = '';
            values[`${variable}_unit`] = getFieldUnit(field, null);
            // For override fields OR optional fields, initialize checkbox to false
            const isOptionalField = !field.required && !field.isOverride;
            if (field.isOverride || isOptionalField) {
              values[`override_${variable}`] = false;
            }
          }
        });
        
        setDynamicFieldValues(values);
        
      } catch (error) {
        console.error('Failed to fetch audit log:', error);
        setDynamicFieldValues({});
      }
    };
    
    populateDynamicFields();
  }, [
    dialogOpen,
    editingEmissionId,
    editingEmission,
    dynamicInputFields,
    getAuthHeader,
    isOriginalEditSelection,
  ]);

  // Check if two unit strings match using centralized unit aliases
  // E1: Pure unit-matching utility (delegates to shared util)
  const unitsMatch = (unit1, unit2) => unitsMatchShared(unit1, unit2, centralizedUnits);

  // E1: Pure volume-unit check (delegates to shared util)
  const isVolumeUnit = (unitStr) => isVolumeUnitShared(unitStr, centralizedUnits);

  // E2: Evidence + history handlers extracted to useEvidenceManagement hook.
  const {
    handleFileUpload,
    handleDeleteExistingEvidence,
    handleDeleteAllEvidences,
    handleRemoveEvidence,
    commitEvidenceChanges,
    discardEvidenceChanges,
    handleViewEvidence,
    handleDownloadEvidence,
  } = useEvidenceManagement({
    existingEvidences,
    uploadedEvidence,
    setFormData,
    setExistingEvidences,
    setUploadedEvidence,
    getAuthHeader,
  });

  const handleC7EditEvidenceUpload = useCallback(async (employeeId, periodKey, file, sha256Hash = null) => {
    const sizeError = validateFileSize(file);
    if (sizeError) throw new Error(sizeError);

    const uploadedEvidence = {
      filename: file.name,
      file,
      is_draft: true,
      sha256_hash: sha256Hash,
    };
      setEditEmployees((currentEmployees) => currentEmployees.map((employee) => {
        if (employee.id !== employeeId) return employee;
        if (periodKey === 'yearly') {
          return {
            ...employee,
            yearly_data: {
              ...(employee.yearly_data || { inputs: {}, emissions: null }),
              evidences: [...(employee.yearly_data?.evidences || []), uploadedEvidence],
            },
          };
        }
        const currentMonth = employee.monthly_data?.[periodKey] || { inputs: {}, emissions: null };
        return {
          ...employee,
          monthly_data: {
            ...employee.monthly_data,
            [periodKey]: {
              ...currentMonth,
              evidences: [...(currentMonth.evidences || []), uploadedEvidence],
            },
          },
        };
      }));
      setIsFormDirty(true);
      const periodLabel = periodKey === 'yearly'
        ? 'annual data'
        : MONTHS.find((month) => month.key === periodKey)?.name || 'the selected period';
      toast.success(`Evidence uploaded for ${periodLabel}`);
  }, [getAuthHeader, setEditEmployees]);

  const handleC7EditEvidenceRemove = useCallback(async (employeeId, periodKey, evidenceIndex) => {
    const employee = editEmployees.find((entry) => entry.id === employeeId);
    const periodData = periodKey === 'yearly'
      ? employee?.yearly_data
      : employee?.monthly_data?.[periodKey];
    const evidenceToRemove = periodData?.evidences?.[evidenceIndex] || null;
    const fileId = evidenceToRemove?.file_id || evidenceToRemove?.url?.match(/\/api\/files\/([a-f0-9-]+)/i)?.[1];
    if (!evidenceToRemove?.is_draft && fileId) {
      setDraftField('c7EvidenceIdsToDelete', (currentIds = []) => (
        currentIds.includes(fileId) ? currentIds : [...currentIds, fileId]
      ));
    }

    setEditEmployees((currentEmployees) => currentEmployees.map((employee) => {
      if (employee.id !== employeeId) return employee;
      const currentPeriodData = periodKey === 'yearly'
        ? employee.yearly_data
        : employee.monthly_data?.[periodKey];
      const remainingEvidences = (currentPeriodData?.evidences || []).filter((_, index) => index !== evidenceIndex);
      if (periodKey === 'yearly') {
        return { ...employee, yearly_data: { ...employee.yearly_data, evidences: remainingEvidences } };
      }
      return {
        ...employee,
        monthly_data: {
          ...employee.monthly_data,
          [periodKey]: { ...currentPeriodData, evidences: remainingEvidences },
        },
      };
    }));
    setIsFormDirty(true);
    toast.success('Evidence removed');
  }, [editEmployees, getAuthHeader, setDraftField, setEditEmployees]);

  const commitC7EvidenceChanges = useCallback(async (emissionId, employeesToCommit) => {
    const drafts = employeesToCommit.flatMap((employee) => {
      const yearly = (employee.yearly_data?.evidences || []).map((evidence) => ({
        evidence, employeeId: employee.id, periodKey: 'yearly',
      }));
      const monthly = Object.entries(employee.monthly_data || {}).flatMap(([periodKey, monthData]) => (
        (monthData?.evidences || []).map((evidence) => ({ evidence, employeeId: employee.id, periodKey }))
      ));
      return [...yearly, ...monthly];
    }).filter(({ evidence }) => evidence?.is_draft && evidence?.file);
    const uploads = await Promise.allSettled(drafts.map(({ evidence, employeeId, periodKey }) => {
      const uploadData = new FormData();
      uploadData.append('file', evidence.file);
      if (evidence.sha256_hash) uploadData.append('sha256_hash', evidence.sha256_hash);
      const query = new URLSearchParams({ employee_id: employeeId, period_key: periodKey });
      return axios.post(`${API}/emissions/${emissionId}/evidence?${query.toString()}`, uploadData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
    }));
    // C7 record persistence owns evidence cleanup; do not bypass shared-file checks via /files.
    const deletions = [];
    const failures = [...uploads, ...deletions].filter((result) => result.status === 'rejected').length;
    if (failures) toast.error(`The record was saved, but ${failures} evidence change(s) could not be completed.`);
  }, [editDraft.c7EvidenceIdsToDelete, getAuthHeader]);

  const fetchHistory = async (emission) => {
    if (isSupplierUser) return;
    try {
      const response = await axios.get(`${API}/emissions/${emission.id}/history`, {
        headers: getAuthHeader()
      });
      setSelectedEmissionHistory(response.data);
      setHistoryDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load version history');
    }
  };

  // Handle fuel selection from database
  const handleFuelSelect = (fuelId) => {
    // A fuel change affects the calculation inputs even when Quantity itself is unchanged.
    // Mark the edit as changed so the calculation effect replaces the persisted result.
    setIsFormDirty(true);
    // Clear dynamic field values when fuel changes to reset units and values
    setDynamicFieldValues({});
    
    if (!fuelId) {
      setFormData(prev => ({
        ...prev,
        fuel_id: '',
        fuel_type: '',
        sub_category: '',
        emission_factor_co2: '',
        emission_factor_ch4: '',
        emission_factor_n2o: '',
        calorific_value: '',
        calorific_value_unit: '',
        density: '',
        density_unit: '',
        conversion_factor: '1',
        source_of_information: ''
      }));
      setOverrideCalorificValue(false);
      setOverrideDensity(false);
      return;
    }

    const fuel = fuelDatabase.find(f => f.id === fuelId);
    if (fuel) {
      // Determine default quantity unit based on fuel's allowed units
      // For energy fuels (like electricity), use energy units by default
      let defaultUnit = 'kg'; // Default mass unit
      if (fuel.allowed_units && fuel.allowed_units.length > 0) {
        // Use centralized units to detect energy units dynamically
        const energyUnitsFromDb = centralizedUnits
          .filter(u => u.unit_type?.toLowerCase() === 'energy')
          .map(u => u.symbol.toLowerCase());
        
        // Also check aliases
        const energyAliases = centralizedUnits
          .filter(u => u.unit_type?.toLowerCase() === 'energy')
          .flatMap(u => (u.aliases || []).map(a => a.toLowerCase()));
        
        const allEnergyUnits = [...energyUnitsFromDb, ...energyAliases];
        
        // Fallback to common energy units if none in database
        const fallbackEnergyUnits = ['kwh', 'mwh', 'gwh', 'tj', 'gj', 'mj'];
        const energyUnitCheck = allEnergyUnits.length > 0 ? allEnergyUnits : fallbackEnergyUnits;
        
        const hasEnergyUnit = fuel.allowed_units.some(u => 
          energyUnitCheck.includes(u.toLowerCase())
        );
        
        if (hasEnergyUnit) {
          // Use the first energy unit as default
          defaultUnit = fuel.allowed_units.find(u => 
            energyUnitCheck.includes(u.toLowerCase())
          ) || fuel.allowed_units[0];
        } else {
          // Use first allowed unit
          defaultUnit = fuel.allowed_units[0];
        }
      }
      
      setFormData(prev => ({
        ...prev,
        fuel_id: fuelId,
        fuel_type: fuel.fuel_name,
        sub_category: fuel.fuel_name,
        emission_factor_co2: fuel.emission_factor_co2?.toString() || '',
        emission_factor_ch4: fuel.emission_factor_ch4?.toString() || '',
        emission_factor_n2o: fuel.emission_factor_n2o?.toString() || '',
        emission_factor_basis_quantity: fuel.emission_factor_basis_quantity?.toString() || '',
        emission_factor_basis_unit: fuel.emission_factor_basis_unit || '',
        calorific_value: fuel.calorific_value?.toString() || '',
        calorific_value_unit: fuel.calorific_value_unit || '',
        density: fuel.density?.toString() || '',
        density_unit: fuel.density_unit || '',
        conversion_factor: fuel.conversion_factor?.toString() || '1',
        source_of_information: fuel.source || '',
        quantity_unit: defaultUnit  // Set default unit based on fuel type
      }));
      setOverrideCalorificValue(false);
      setOverrideDensity(false);
    }
  };

  // Get the selected facility's sector and country for filtering fuels
  const selectedFacilitySector = useMemo(() => {
    const facility = facilities.find(f => f.id === formData.facility_id);
    return facility?.sector || (isSupplierUser ? 'Manufacturing' : '');
  }, [facilities, formData.facility_id, isSupplierUser]);

  const selectedFacilityCountry = useMemo(() => {
    const facility = facilities.find(f => f.id === formData.facility_id);
    return facility?.country || '';
  }, [facilities, formData.facility_id]);

  // Extract reporting year from reporting period for year-based filtering
  const reportingYearFromPeriod = useMemo(() => {
    // Parse year from reporting_period_start (format: "YYYY-MM" or "2025-04")
    if (formData.reporting_period_start) {
      const yearMatch = formData.reporting_period_start.match(/^(\d{4})/);
      if (yearMatch) {
        return parseInt(yearMatch[1], 10);
      }
    }
    return new Date().getFullYear();
  }, [formData.reporting_period_start]);

  // Check if editing a C7 (Employee Commuting) category
  const isEditC7EmployeeCommuting = useMemo(() => {
    if (formData.scope !== 'scope3') return false;
    return editCapabilities.multiEmployee;
  }, [formData.scope, formData.category, editCapabilities]);

  // Pluggable category renderer + edit-flow lookup. The page delegates
  // the dynamic-fields portion of the Scope 3 edit dialog to the module
  // returned (via `module.DynamicFieldsRenderer`), and the save flow to
  // `module.buildEditPayload`.
  //
  // Resolution:
  //   - Scope 3 explicit            → match by category code (c1..c15)
  //   - Biogenic + scope3 selection → generic Scope 3 module
  //   - Scope 1                     → match by category name
  //                                   (Stationary/Mobile/Fugitive Combustion)
  //                                   or generic Scope 1 fallback
  //   - Biogenic + scope1 selection → generic Scope 1 module
  //   - All other scopes/states     → legacy inline path (returns null)
  const activeCategoryModule = useMemo(() => {
    const cat = (formData.category || '').toLowerCase();

    // 1. Plain Scope 3 → match by category code; if no match, stay on legacy
    if (formData.scope === 'scope3') {
      const codeMatch = cat.match(/^(c\d+)/);
      if (!codeMatch) return null;
      return categoryRegistry.get(codeMatch[1]) || null;
    }

    // 2. Biogenic + scope3 selection → generic Scope 3 (same payload shape)
    if (formData.scope === 'biogenic' && biogenicScopeSelection === 'scope3') {
      return categoryRegistry.getGenericModule?.('scope3') || null;
    }

    // 3. Scope 1 → match by category name
    if (formData.scope === 'scope1') {
      if (cat.includes('stationary')) return categoryRegistry.get('stationary_combustion');
      if (cat.includes('mobile')) return categoryRegistry.get('mobile_combustion');
      if (cat.includes('fugitive')) return categoryRegistry.get('fugitive_emissions');
      return categoryRegistry.getGenericModule?.('scope1') || null;
    }

    // 4. Scope 2 → single generic module (purchased electricity/steam/heating/cooling)
    if (formData.scope === 'scope2') {
      return categoryRegistry.getGenericModule?.('scope2') || null;
    }

    // 5. Biogenic + scope1 selection → generic Scope 1
    if (formData.scope === 'biogenic' && biogenicScopeSelection === 'scope1') {
      return categoryRegistry.getGenericModule?.('scope1') || null;
    }

    // 6. Unknown → legacy path (safety fallback)
    return null;
  }, [formData.scope, formData.category, biogenicScopeSelection]);

  const ModuleDynamicFieldsRenderer = activeCategoryModule?.DynamicFieldsRenderer || null;

  // Clear employee calculations when activity changes for C7 edit
  // This forces users to recalculate with the new activity's emission factor
  const isInitialLoadRef = useRef(true);
  useEffect(() => {
  // 1. If the dialog is closed, reset the flag so it's ready for the next time you edit a record.
  if (!dialogOpen) {
    isInitialLoadRef.current = true;
    return;
  }

  // 2. If the dialog is open but this is the FIRST time the effect is running, 
  // skip the clearing logic and flip the flag.
  if (isInitialLoadRef.current) {
    isInitialLoadRef.current = false;
    return; 
  }

  // 3. If we get here, it means the user manually changed the activity dropdown. 
  // Now it is safe to clear the old calculations.
  if (isEditC7EmployeeCommuting && editEmployees.length > 0) {
    setEditEmployees(prevEmployees => prevEmployees.map(emp => ({
      ...emp,
      emissions: null,
      calculation_details: null,
      monthly_data: Object.fromEntries(
        Object.entries(emp.monthly_data || {}).map(([month, data]) => [
          month,
          { ...data, emissions: null, calculation_details: null }
        ])
      ),
      yearly_data: emp.yearly_data ? {
        ...emp.yearly_data,
        emissions: null,
        calculation_details: null,
      } : null,
    })));
  }
// Note: added dialogOpen and isEditC7EmployeeCommuting to the dependency array to avoid ESLint warnings
}, [scope3ActivityId, scope3ActivityType, dialogOpen, isEditC7EmployeeCommuting]);

  // Active months for C7 Employee Commuting edit (based on reporting period)
  const editActiveMonths = useMemo(() => {
    // For C7 records, extract months from the employees data
    if (!editingEmission || !isEditC7EmployeeCommuting) return [];

    if (editFrequencyType === 'yearly') return ['yearly'];
    
    // For new monthly model, only show the single month being edited
    if (editC7Month) {
      return [editC7Month];
    }

    // A monthly emission record represents exactly its reporting month,
    // including a record converted to C7 during Edit.
    const reportingMonthKey = resolveC7ReportingMonth(
      formData.reporting_period_start || editingEmission.reporting_period,
    );
    if (reportingMonthKey) return [reportingMonthKey];
    
    // Try to determine months from the employees data (old model with monthly_data)
    const monthsWithData = new Set();
    editEmployees.forEach(emp => {
      Object.keys(emp.monthly_data || {}).forEach(monthKey => {
        monthsWithData.add(monthKey);
      });
    });
    
    if (monthsWithData.size > 0) {
      const monthOrder = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
      return Array.from(monthsWithData).sort((a, b) => monthOrder.indexOf(a) - monthOrder.indexOf(b));
    }
    
    // Fallback to all 12 months
    return ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  }, [
    editingEmission,
    isEditC7EmployeeCommuting,
    editEmployees,
    editC7Month,
    editFrequencyType,
    formData.reporting_period_start,
    editingEmission?.reporting_period,
  ]);

  useEffect(() => {
    if (
      !dialogOpen
      || !isEditC7EmployeeCommuting
      || editFrequencyType === 'yearly'
      || editC7Month
    ) {
      return;
    }

    const reportingMonthKey = resolveC7ReportingMonth(
      formData.reporting_period_start || editingEmission?.reporting_period,
    );
    if (reportingMonthKey) setEditC7Month(reportingMonthKey);
  }, [
    dialogOpen,
    isEditC7EmployeeCommuting,
    editFrequencyType,
    editC7Month,
    formData.reporting_period_start,
    editingEmission?.reporting_period,
    setEditC7Month,
  ]);

  /**
   * Helper function to apply region + year priority fallback
   * Priority order:
   * 1. Region-specific + exact year match
   * 2. Region-specific + most recent year before reporting year
   * 3. Region-specific + null year
   * 4. Global + exact year match
   * 5. Global + most recent year before reporting year  
   * 6. Global + null year
   * 7. Any available fallback
   */
  const selectBestFuelMatch = useCallback((fuels, facilityCountry, targetYear) => {
    if (!fuels || fuels.length === 0) return null;
    
    // Separate fuels by region
    const regionSpecific = facilityCountry 
      ? fuels.filter(f => f.region && f.region.toLowerCase() === facilityCountry.toLowerCase())
      : [];
    const globalFuels = fuels.filter(f => f.region?.toLowerCase() === 'global' || !f.region);
    const otherFuels = fuels.filter(f => 
      f.region && f.region.toLowerCase() !== 'global' && 
      (!facilityCountry || f.region.toLowerCase() !== facilityCountry.toLowerCase())
    );
    
    // Helper to find best year match within a fuel group
    const findBestYearMatch = (fuelGroup) => {
      if (fuelGroup.length === 0) return null;
      
      // 1. Exact year match
      const exactYear = fuelGroup.find(f => f.year_applicable === targetYear);
      if (exactYear) return exactYear;
      
      // 2. Most recent year before target year
      const earlierYears = fuelGroup
        .filter(f => f.year_applicable && f.year_applicable < targetYear)
        .sort((a, b) => b.year_applicable - a.year_applicable);
      if (earlierYears.length > 0) return earlierYears[0];
      
      // 3. Null year (timeless data)
      const nullYear = fuelGroup.find(f => !f.year_applicable || f.year_applicable === null);
      if (nullYear) return nullYear;
      
      // 4. Any year (future years as last resort)
      const anyYear = fuelGroup.sort((a, b) => 
        (a.year_applicable || 9999) - (b.year_applicable || 9999)
      );
      return anyYear[0] || null;
    };
    
    // Apply priority: Region-specific > Global > Other
    let bestMatch = findBestYearMatch(regionSpecific);
    if (bestMatch) return bestMatch;
    
    bestMatch = findBestYearMatch(globalFuels);
    if (bestMatch) return bestMatch;
    
    bestMatch = findBestYearMatch(otherFuels);
    if (bestMatch) return bestMatch;
    
    // Absolute fallback
    return fuels[0];
  }, []);

  // Get fuels filtered by scope, industry, category, region, and year (with priority fallback)
  const getFuelsForScope = useMemo(() => {
    let filtered = fuelDatabase.filter(f => f.scope === formData.scope);
    
    // If a facility is selected and has a sector, filter fuels by industry
    if (selectedFacilitySector) {
      filtered = filtered.filter(fuel => {
        // Check if fuel has industry_sectors array (new format)
        if (fuel.industry_sectors && fuel.industry_sectors.length > 0) {
          return fuel.industry_sectors.some(sector => 
            sector.toLowerCase() === selectedFacilitySector.toLowerCase()
          );
        }
        // Fall back to legacy industry_sector field
        if (fuel.industry_sector) {
          return fuel.industry_sector.toLowerCase() === selectedFacilitySector.toLowerCase();
        }
        // If no industry filter on fuel, show it (backwards compatibility)
        return true;
      });
    }
    
    // Group fuels by unique identifier (name + category) to handle region/year variants
    // Support both categories array and legacy category field
    const fuelsByKey = {};
    filtered.forEach(fuel => {
      const fuelCategories = fuel.categories?.length > 0 ? fuel.categories : (fuel.category ? [fuel.category] : ['uncategorized']);
      fuelCategories.forEach(cat => {
        const key = `${fuel.fuel_name}_${cat}`;
        if (!fuelsByKey[key]) {
          fuelsByKey[key] = [];
        }
        fuelsByKey[key].push(fuel);
      });
    });
    
    // For each fuel name+category, select the best match based on region and year priority
    const prioritizedFuels = [];
    const addedFuelIds = new Set(); // Prevent duplicates when fuel appears in multiple categories
    Object.values(fuelsByKey).forEach(fuels => {
      const bestMatch = selectBestFuelMatch(fuels, selectedFacilityCountry, reportingYearFromPeriod);
      if (bestMatch && !addedFuelIds.has(bestMatch.id)) {
        prioritizedFuels.push(bestMatch);
        addedFuelIds.add(bestMatch.id);
      }
    });
    
    return prioritizedFuels;
  }, [fuelDatabase, formData.scope, selectedFacilitySector, selectedFacilityCountry, reportingYearFromPeriod, selectBestFuelMatch]);

  // ============================================================================
  // SCOPE 3 SPECIFIC COMPUTED PROPERTIES FOR INLINE EDIT FORM
  // ============================================================================
  
  const requiresSubcategory = useMemo(() => {
    // Handle both regular scope3 and biogenic with scope3 selection
    const isScope3 = formData.scope === 'scope3';
    const isBiogenicScope3 = formData.scope === 'biogenic' && biogenicScopeSelection === 'scope3';
    
    if ((!isScope3 && !isBiogenicScope3) || !selectedCategory) return false;
    
    // For biogenic, subcategory is not required (biogenic fuels are categorized by sub_scope, not subcategory)
    if (isBiogenicScope3) return false;
    
    return editCapabilities.subcategory;
  }, [formData.scope, selectedCategory, biogenicScopeSelection, editCapabilities]);

  const editScope3PresentationOptions = useMemo(() => resolveGhgScope3Options({
    scope: formData.scope,
    biogenicScopeSelection,
    category: selectedCategory,
    scope3Method,
    scope3EFData,
    capabilities: editCapabilities,
    requiresSubcategory,
    fieldOptions: editGhgFieldOptions,
    configLabels: configLabels?.subcategories || {},
  }), [
    formData.scope, biogenicScopeSelection, selectedCategory, scope3Method,
    scope3EFData, editCapabilities, requiresSubcategory, editGhgFieldOptions, configLabels?.subcategories,
  ]);

  const availableScope3Methods = editScope3PresentationOptions.methods;
  const availableScope3ActivityTypes = editScope3PresentationOptions.activityTypes;

  // Get available subcategories for C8/C10/C11/C13/C14
  const availableSubcategories = editScope3PresentationOptions.subcategories;

  // Filter Scope 3 activities based on category, method, activity_type, subcategory, industry sector
  const filteredScope3Activities = useMemo(() => {
    // Handle both regular scope3 and biogenic with scope3 selection
    const isScope3 = formData.scope === 'scope3';
    const isBiogenicScope3 = activeScope === 'biogenic' && biogenicScopeSelection === 'scope3';
    
    if (!isScope3 && !isBiogenicScope3) return [];
    if (!scope3EFData.length) return [];
    
    // For biogenic, filter by sub_scope='biogenic'
    let baseData = scope3EFData;
    if (isBiogenicScope3) {
      baseData = scope3EFData.filter(ef => ef.sub_scope === 'biogenic');
    }
    
    // Get facility for sector filtering
    const facility = facilities.find(f => f.id === formData.facility_id);
    const catLower = selectedCategory?.toLowerCase() || '';
    const isSubcategoryCategory = editCapabilities.subcategory;
    
    // For BIOGENIC scope3, skip subcategory handling - just filter by category directly
    // Biogenic C8/C10/C11/C13/C14 should work like C3 (direct activity selection)
    if (isBiogenicScope3 && isSubcategoryCategory) {
      // Filter biogenic activities by category
      let filtered = baseData.filter(ef => 
        ef.category?.toLowerCase() === catLower
      );
      
      // Filter by method
      if (scope3Method && scope3Method !== 'supplier_basis') {
        filtered = filtered.filter(ef => ef.method === scope3Method);
      }
      
      return selectClosestScope3Factors(filtered, reportingYearFromPeriod);
    }
    
    // For REGULAR scope3 with subcategory-based categories (C8, C10, C11, C13, C14), handle specially
    if (isSubcategoryCategory && scope3Subcategory) {
      // For fugitive_emissions, return data from fugitiveEmissionsData instead
      if (scope3Subcategory === 'fugitive_emissions') {
        return fugitiveEmissionsData.map(f => ({
          ...f,
          method: scope3Method,
          category: selectedCategory
        }));
      }
      
      // For stationary_combustion, mobile_combustion, and energy, filter from scope3_ef
      if (scope3Subcategory === 'stationary_combustion' || 
          scope3Subcategory === 'mobile_combustion' || 
          scope3Subcategory === 'energy' ||
          scope3Subcategory === 'electricity') {  // Support legacy 'electricity' value
        let filtered = baseData.filter(ef => 
          ef.category?.toLowerCase() === catLower
        );
        
        // Filter by subcategory field if it exists on the entry
        // For energy: ONLY show entries with exact match (no fallback to null/empty)
        // For stationary/mobile: If entry has no subcategory defined, show in both
        const energySubcategory = scope3Subcategory === 'electricity' ? 'energy' : scope3Subcategory;
        filtered = filtered.filter(ef => {
          if (energySubcategory === 'energy') {
            // Strict matching - only show entries explicitly marked as energy
            // Also support legacy 'electricity' entries
            if (Array.isArray(ef.subcategory)) {
              return ef.subcategory.includes('energy') || ef.subcategory.includes('electricity');
            }
            return ef.subcategory === 'energy' || ef.subcategory === 'electricity';
          }
          
          // For stationary/mobile: fallback to null/empty subcategory
          if (!ef.subcategory || ef.subcategory.length === 0) {
            return true;
          }
          if (Array.isArray(ef.subcategory)) {
            return ef.subcategory.includes(scope3Subcategory);
          }
          return ef.subcategory === scope3Subcategory;
        });
        
        // Filter by method
        if (scope3Method && scope3Method !== 'supplier_basis') {
          filtered = filtered.filter(ef => ef.method === scope3Method);
        }
        
        return selectClosestScope3Factors(filtered, reportingYearFromPeriod);
      }
    }
    
    // Standard filtering for non-subcategory categories
    let filtered = [...baseData];
    
    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter(ef => 
        ef.category?.toLowerCase() === selectedCategory.toLowerCase()
      );
    }
    
    // Filter by method - for supplier_basis, show ALL activities for the category
    // For spend_basis/activity_basis, filter by specific method
    if (scope3Method && scope3Method !== 'supplier_basis') {
      filtered = filtered.filter(ef => ef.method === scope3Method);
    }
    
    // Filter by activity_type for categories that expose an Activity Type selector.
    if (scope3ActivityType) {
      filtered = filtered.filter((ef) => ef.activity_type === scope3ActivityType);
    }
    
    // Filter by industry sector (if facility has one)
    if (facility?.sector) {
      filtered = filtered.filter(ef => {
        if (ef.industry_sectors && ef.industry_sectors.length > 0) {
          return ef.industry_sectors.some(s => 
            s.toLowerCase() === facility.sector.toLowerCase()
          );
        }
        return true;
      });
    }
    
    return selectClosestScope3Factors(filtered, reportingYearFromPeriod);
  }, [formData.scope, formData.facility_id, scope3EFData, selectedCategory, scope3Method, scope3ActivityType, scope3Subcategory, fugitiveEmissionsData, facilities, activeScope, biogenicScopeSelection, editCapabilities, reportingYearFromPeriod]);

  useEffect(() => {
    if (!scope3ActivityId || filteredScope3Activities.some((activity) => activity.id === scope3ActivityId)) return;
    const previouslySelected = scope3EFData.find((activity) => activity.id === scope3ActivityId);
    const replacement = filteredScope3Activities.find((activity) => (
      previouslySelected?.activity
      && activity.activity?.toLowerCase() === previouslySelected.activity.toLowerCase()
    ));
    if (replacement) setScope3ActivityId(replacement.id);
  }, [filteredScope3Activities, scope3ActivityId, scope3EFData, setScope3ActivityId]);

  // Get unique categories for the scope
  const standardCategoriesForScope = useMemo(() => {
    // For biogenic with scope3 selected, return only biogenic categories
    if (activeScope === 'biogenic' && biogenicScopeSelection === 'scope3') {
      return biogenicCategories.sort((a, b) => {
        const numA = parseInt(a.match(/C(\d+)/)?.[1] || '999');
        const numB = parseInt(b.match(/C(\d+)/)?.[1] || '999');
        return numA - numB;
      });
    }
    
    // For Scope 3, get categories from dynamicCategories that belong to Scope 3
    if (formData.scope === 'scope3') {
      const scope3 = dynamicScopes.find(s => s.code === 'scope3');
      if (scope3) {
        const scope3Cats = dynamicCategories
          .filter(c => c.scope_id === scope3.id)
          .map(c => c.name);
        // Sort by category number (C1, C2, ... C15)
        return scope3Cats.sort((a, b) => {
          const numA = parseInt(a.match(/C(\d+)/)?.[1] || '999');
          const numB = parseInt(b.match(/C(\d+)/)?.[1] || '999');
          return numA - numB;
        });
      }
      // Fallback: get unique categories from Scope 3 EF data
      const cats = new Set();
      scope3EFData.forEach(ef => {
        if (ef.category) cats.add(ef.category);
      });
      // Sort by category number (C1, C2, ... C15)
      return Array.from(cats).sort((a, b) => {
        const numA = parseInt(a.match(/C(\d+)/)?.[1] || '999');
        const numB = parseInt(b.match(/C(\d+)/)?.[1] || '999');
        return numA - numB;
      });
    }
    
    // Scope 1 combines fuel categories with configured categories. Process
    // Emissions is decision-tree based, so it has no fuel database row.
    const cats = new Set();
    getFuelsForScope.forEach(f => {
      // Support both categories array and legacy category field
      if (f.categories?.length > 0) {
        f.categories.forEach(c => cats.add(c));
      } else if (f.category) {
        cats.add(f.category);
      }
    });

    const configuredScope = formData.scope === 'biogenic' && biogenicScopeSelection === 'scope1'
      ? 'scope1'
      : formData.scope;
    dynamicCategories
      .filter(category => category.scope_code === configuredScope)
      .forEach(category => cats.add(category.name));
    if (formData.category) {
      cats.add(formData.category);
    }
    
    return Array.from(cats).sort();
  }, [formData.scope, formData.category, getFuelsForScope, dynamicScopes, dynamicCategories, scope3EFData, activeScope, biogenicScopeSelection, biogenicCategories]);

  const getCategoriesForScope = useMemo(() => resolveGhgCategoryOptions({
    standardCategories: isSupplierUser
      ? filterSupplierVisibleCategories(standardCategoriesForScope, resolveEffectiveScopeCode(formData.scope, biogenicScopeSelection), supplierReportingConfig)
      : standardCategoriesForScope,
    scopeCode: resolveEffectiveScopeCode(formData.scope, biogenicScopeSelection),
    categoryDefinitions: dynamicCategories,
    organizationOverrides: effectiveSupplierGhgOverrides,
  }), [standardCategoriesForScope, formData.scope, biogenicScopeSelection, dynamicCategories, isSupplierUser, supplierReportingConfig, effectiveSupplierGhgOverrides]);

  // Get fuels for selected category
  const getFuelsForCategory = useMemo(() => {
    if (!selectedCategory) return [];
    let fuels = getFuelsForScope.filter(f => {
      const fuelCategories = f.categories?.length > 0 ? f.categories : (f.category ? [f.category] : []);
      return fuelCategories.includes(selectedCategory);
    });
    
    // IMPORTANT: When editing, ensure the saved fuel is always included in the list
    // The fuel prioritization logic may select a different variant (region/year), 
    // but we need to show the originally saved fuel so it appears selected
    if (formData.fuel_id && !fuels.some(f => f.id === formData.fuel_id)) {
      const savedFuel = fuelDatabase.find(f => f.id === formData.fuel_id);
      if (savedFuel) {
        fuels = [savedFuel, ...fuels];
      }
    }
    
    return fuels;
  }, [getFuelsForScope, selectedCategory, formData.fuel_id, fuelDatabase]);

  // Group fuels by category for better organization (keeping for filter dropdown)
  const getFuelsByCategory = useMemo(() => {
    const grouped = {};
    getFuelsForScope.forEach(fuel => {
      const fuelCategories = fuel.categories?.length > 0 ? fuel.categories : (fuel.category ? [fuel.category] : []);
      fuelCategories.forEach(cat => {
        if (!grouped[cat]) {
          grouped[cat] = [];
        }
        grouped[cat].push(fuel);
      });
    });
    return grouped;
  }, [getFuelsForScope]);

  // Get available quantity units from centralized units module, filtered by fuel's allowed units
  const availableQuantityUnits = useMemo(() => {
    // Get selected fuel's allowed units
    const selectedFuel = fuelDatabase.find(f => f.id === formData.fuel_id);
    // Filter out 'm3' from allowed units - use 'm³' instead (proper superscript notation)
    const fuelAllowedUnits = selectedFuel?.allowed_units?.filter(u => u !== 'm3') || null;
    
    // Build units list from centralized units
    let units = [];
    
    if (centralizedUnits.length > 0) {
      // Use centralized units
      centralizedUnits.forEach(unit => {
        units.push({
          value: unit.symbol,
          label: `${unit.name} (${unit.symbol})`,
          type: unit.unit_type,
          requiresDensity: unit.unit_type === 'volume',
          conversionToBase: unit.conversion_to_base,
          isBaseUnit: unit.is_base_unit,
          aliases: unit.aliases || []
        });
      });
    } else {
      // Fallback: use kg as default
      units = [{ value: 'kg', label: 'Kilograms (kg)', type: 'mass' }];
    }
    
    // If fuel has allowed_units, filter to only show those
    if (fuelAllowedUnits && fuelAllowedUnits.length > 0) {
      units = units.filter(u => 
        fuelAllowedUnits.some(allowed => unitsMatch(allowed, u.value))
      );
      // Ensure at least kg is available if nothing matches
      if (units.length === 0) {
        const kgUnit = centralizedUnits.find(u => u.symbol === 'kg');
        units = [{ 
          value: 'kg', 
          label: kgUnit ? `${kgUnit.name} (kg)` : 'Kilograms (kg)', 
          type: 'mass' 
        }];
      }
    }
    
    return units;
  }, [centralizedUnits, fuelDatabase, formData.fuel_id]);

  // Determine if current quantity unit is a mass or volume unit
  const currentUnitType = useMemo(() => {
    const unit = availableQuantityUnits.find(u => 
      u.value.toLowerCase() === formData.quantity_unit.toLowerCase()
    );
    return unit?.type || 'mass';
  }, [availableQuantityUnits, formData.quantity_unit]);

  // Get the conversion factor for a parameter based on the selected unit
  // The Super Admin defines conversions as: "X from_unit = 1 to_unit" (e.g., 1000 g = 1 kg)
  // So the multiplier represents how many from_units make 1 to_unit
  // To convert: divide the value by the multiplier (e.g., 1000g / 1000 = 1kg)
  // E1: Conversion factor lookup (delegates to shared util)
  const getConversionFactor = (paramKey, selectedUnit) =>
    getConversionFactorShared(paramKey, selectedUnit, formulaParameters);

  // E1: Conversion-defined check (delegates to shared util)
  const hasConversionDefined = (paramKey, selectedUnit) =>
    hasConversionDefinedShared(paramKey, selectedUnit, formulaParameters);

  // Convert quantity to kg based on selected unit (now uses dynamic units)
  const getQuantityInKg = useMemo(() => {
    const quantity = parseFloat(formData.quantity) || 0;
    const unit = availableQuantityUnits.find(u => u.value.toLowerCase() === formData.quantity_unit.toLowerCase());
    
    if (!unit) return quantity; // Default to assuming kg
    
    if (unit.requiresDensity) {
      const density = parseFloat(formData.density) || 1;
      // Use the conversion factor from Super Admin
      const convFactor = getConversionFactor('quantity_fuel', formData.quantity_unit);
      return quantity * convFactor * density;
    }
    
    // Use the conversion factor from Super Admin
    const convFactor = getConversionFactor('quantity_fuel', formData.quantity_unit);
    return quantity * convFactor;
  }, [formData.quantity, formData.quantity_unit, formData.density, availableQuantityUnits]);

  // Get the selected fuel data for dynamic mappings
  const selectedFuel = useMemo(() => {
    if (!formData.fuel_id) return null;
    return fuelDatabase.find(f => f.id === formData.fuel_id);
  }, [formData.fuel_id, fuelDatabase]);

  // Persist the same default unit that every visible dynamic selector uses.
  // This keeps all Scope 1–3 calculation previews and later Save payloads in
  // sync before a user manually changes a unit.
  useEffect(() => {
    if (!dialogOpen || dynamicInputFields.length === 0) return;

    const isScope3Like = formData.scope === 'scope3'
      || (formData.scope === 'biogenic' && biogenicScopeSelection === 'scope3');
    const matchedScope3Activity = scope3ActivityId
      ? filteredScope3Activities.find((activity) => activity.id === scope3ActivityId)
      : null;

    setDynamicFieldValues((currentValues) => {
      const nextValues = { ...currentValues };
      let changed = false;

      dynamicInputFields.forEach((field) => {
        const unitKey = `${field.variable}_unit`;
        if (field.unitSource === 'none' || field.variable?.endsWith('_unit') || currentValues[unitKey]) return;

        let visibleUnits = [];
        if (field.unitSource === 'fuel') {
          const factorForUnit = isScope3Like && requiresSubcategory && !selectedFuel
            ? matchedScope3Activity
            : selectedFuel;
          visibleUnits = factorForUnit?.allowed_units || [];
        } else if (field.unitSource === 'all_units') {
          visibleUnits = centralizedUnits.map((unit) => unit.symbol);
        } else if (field.unitSource === 'scope3_ef') {
          visibleUnits = matchedScope3Activity?.allowed_units?.length > 0
            ? matchedScope3Activity.allowed_units
            : field.allowedUnits?.length > 0
              ? field.allowedUnits
              : [field.expectedUnit].filter(Boolean);
        } else {
          visibleUnits = field.allowedUnits?.length > 0
            ? field.allowedUnits
            : [field.expectedUnit].filter(Boolean);
        }

        const displayDefaultUnit = visibleUnits[0] || field.expectedUnit || '';
        if (!displayDefaultUnit) return;
        nextValues[unitKey] = displayDefaultUnit;
        changed = true;
      });

      return changed ? nextValues : currentValues;
    });
  }, [
    dialogOpen,
    formData.scope,
    biogenicScopeSelection,
    dynamicInputFields,
    scope3ActivityId,
    filteredScope3Activities,
    selectedFuel,
    requiresSubcategory,
    centralizedUnits,
    setDynamicFieldValues,
  ]);

  // E5: Dead-code block removed (getParameterValueDynamic, getParameterValue,
  // findFormulaForScope, executeFormula) — these were never called from production
  // paths after migration to backend calc engine. Saved ~304 lines.


  // Legacy calculation removed - using backend calc engine exclusively
  const calculatedEmissions = null;

  // ============================================================================
  // Backend Calculation Engine Integration (Phase 3) - NOW ACTIVE
  // Decision trees are configured for all categories, using backend as primary
  // ============================================================================
  // CALCULATION ENGINE - via custom hook (Phase 2 refactor)
  // ============================================================================
  const {
    backendCalcResult,
    setBackendCalcResult,
    calcEngineUsed,
    setCalcEngineUsed,
    isCalculatingNetwork,
    calculationError,
    setCalculationError,
    calculate: triggerCalcEngine,
    clearResult: clearCalcResult
  } = useEmissionsCalculator(getAuthHeader);
  
  const [useBackendCalc, setUseBackendCalc] = useState(true);
  const [liveCalculationValidationError, setLiveCalculationValidationError] = useState('');

  const resetEditCalculationPreview = useCallback(() => {
    setEditFormConfig(null);
    setBackendCalcResult(null);
    clearCalcResult();
    setLiveCalculationValidationError('');
  }, [clearCalcResult, setBackendCalcResult]);

  const handleCategorySelect = useCallback((category) => {
    setHasChangedCalculationSelection(true);
    setEditDraft((currentDraft) => resetEmissionDraftForSelectionChange(currentDraft, {
      scope: currentDraft.values.scope,
      category,
      biogenicScopeSelection: currentDraft.values.scope === 'biogenic'
        ? currentDraft.biogenicScopeSelection
        : '',
      c7Month: /^c7\b/i.test(category)
        ? resolveC7ReportingMonth(currentDraft.values.reporting_period_start || editingEmission?.reporting_period)
        : null,
    }));
    setActivitySearchTerm('');
    resetEditCalculationPreview();
    setIsFormDirty(true);
  }, [resetEditCalculationPreview, editingEmission?.reporting_period]);

  const handleEditScopeChange = useCallback((scope) => {
    setHasChangedCalculationSelection(true);
    setEditDraft((currentDraft) => resetEmissionDraftForSelectionChange(currentDraft, { scope }));
    setActivitySearchTerm('');
    resetEditCalculationPreview();
    setIsFormDirty(true);
  }, [resetEditCalculationPreview]);

  const handleEditBiogenicScopeChange = useCallback((biogenicScopeSelection) => {
    setHasChangedCalculationSelection(true);
    setEditDraft((currentDraft) => resetEmissionDraftForSelectionChange(currentDraft, {
      scope: 'biogenic',
      biogenicScopeSelection,
    }));
    setActivitySearchTerm('');
    resetEditCalculationPreview();
    setIsFormDirty(true);
  }, [resetEditCalculationPreview]);

  const handleEditScope3MethodChange = useCallback((scope3Method) => {
    setHasChangedCalculationSelection(true);
    setEditDraft((currentDraft) => resetEmissionDraftForScope3MethodChange(currentDraft, scope3Method));
    setActivitySearchTerm('');
    resetEditCalculationPreview();
    setIsFormDirty(true);
  }, [resetEditCalculationPreview]);

  useEffect(() => {
    if (!dialogOpen || !editingEmission?.id) return;
    clearCalcResult();
    setBackendCalcResult(null);
    setLiveCalculationValidationError('');
  }, [
    dialogOpen,
    editingEmission?.id,
    formData.scope,
    formData.category,
    clearCalcResult,
    setBackendCalcResult,
  ]);

  const handleC8AllocationMethodChange = useCallback((allocationMethod) => {
    setEditDraft((currentDraft) => ({
      ...currentDraft,
      allocationMethod,
      scope3Subcategory: '',
      scope3ActivityId: '',
      dynamicFieldValues: {},
    }));
    setBackendCalcResult(null);
    setIsFormDirty(true);
  }, [setBackendCalcResult]);
  
  // Effect to trigger backend calculations when inputs change
  // Uses dynamic input fields from calculation engine configuration
  useEffect(() => {
    // Skip if dialog not open
    if (!dialogOpen) {
      clearCalcResult();
      return;
    }

    // C7 owns its calculation and validation at employee-period level. Its
    // configured fields describe each employee row, so running the generic
    // record-level preview against dynamicFieldValues produces false missing
    // field warnings when a record is converted to Employee Commuting.
    if (isEditC7EmployeeCommuting) {
      clearCalcResult();
      setBackendCalcResult(null);
      setLiveCalculationValidationError('');
      return;
    }

    // Stored records already show their persisted/audited result. Do not issue a
    // transient calculation while their category, decision fields, units and
    // dynamic values are still hydrating; calculate after an actual Edit only.
    if (editingEmission && !isFormDirty) {
      return;
    }
    
    // Determine if this is a scope3-like flow (regular scope3 or biogenic scope3)
    const isBiogenicScope3Edit = formData.scope === 'biogenic' && biogenicScopeSelection === 'scope3';
    const isScope3LikeEdit = formData.scope === 'scope3' || isBiogenicScope3Edit;
    
    // For Scope 3 (or biogenic scope3), we need method and activity selected instead of fuel
    // For supplier_basis with custom activity, we need the custom activity name
    // For other scopes, we need fuel selected
    if (isScope3LikeEdit) {
      if (!scope3Method) {
        setBackendCalcResult(null);
        return;
      }
      const isC8AllocationApplicable = formData.scope === 'scope3'
        && /^c8\b/i.test(selectedCategory || formData.category || '')
        && ['activity_basis', 'supplier_basis'].includes(scope3Method);
      if (isC8AllocationApplicable && !editDraft.allocationMethod) {
        setBackendCalcResult(null);
        return;
      }
      // For supplier_basis with custom activity, check custom activity name
      // For supplier_basis without custom activity or other methods, check activity ID
      if (scope3Method === 'supplier_basis' && useCustomActivity) {
        if (!scope3CustomActivity?.trim()) {
          setBackendCalcResult(null);
          return;
        }
      } else if (!scope3ActivityId) {
        setBackendCalcResult(null);
        return;
      }
    } else {
      if (editCapabilities.requiresFuel && !editUseCustomFuel && !selectedFuel) {
        setBackendCalcResult(null);
        return;
      }
    }
    
    // Check if we have dynamic input fields - if so, use them for calculation
    if (dynamicInputFields.length > 0) {
      const unwrapFieldValue = (field) => {
        const rawValue = dynamicFieldValues[field.variable] ?? dynamicFieldValues[field.fieldKey];
        return rawValue && typeof rawValue === 'object' ? rawValue.value : rawValue;
      };
      const isBlankFieldValue = (value) => (
        value === undefined || value === null || String(value).trim() === ''
      );
      const missingRequiredField = dynamicInputFields.find((field) => {
        if (!field.required || field.isOverride || field.presentationOnly) return false;
        const value = unwrapFieldValue(field);
        return isBlankFieldValue(value)
          || ((field.fieldType === 'number' || !field.fieldType) && !Number.isFinite(Number.parseFloat(value)));
      });
      const isC8FloorShare = formData.scope === 'scope3'
        && /^c8\b/i.test(selectedCategory || formData.category || '')
        && editDraft.allocationMethod === 'floor_area_share';
      const floorShareField = isC8FloorShare
        ? dynamicInputFields.find((field) => /floor.*(?:area|share)|(?:area|share).*floor/i.test(
          `${field.variable || ''} ${field.fieldKey || ''} ${field.label || ''}`,
        ))
        : null;
      const missingFloorShare = floorShareField && isBlankFieldValue(unwrapFieldValue(floorShareField));
      const missingInputLabel = missingRequiredField?.label
        || missingRequiredField?.variable
        || (missingFloorShare ? floorShareField.label : 'Floor Share %');
      if (missingRequiredField || missingFloorShare) {
        setLiveCalculationValidationError(`${missingInputLabel} is required before emissions can be calculated.`);
        setBackendCalcResult(null);
        return;
      }
      setLiveCalculationValidationError('');

      // Ensure dynamicFieldValues is populated (not just initialized)
      const hasAnyValues = Object.keys(dynamicFieldValues).some(key => {
        const val = dynamicFieldValues[key];
        return val !== undefined && val !== null && val !== '';
      });
      
      if (!hasAnyValues) {
        setBackendCalcResult(null);
        return;
      }
      
      // Build inputs from dynamicFieldValues
      const inputs = {};
      let hasValidInput = false;
      const matchedActivityForEdit = scope3ActivityId ? filteredScope3Activities.find(a => a.id === scope3ActivityId) : null;
      
      dynamicInputFields.forEach(field => {
        const value = dynamicFieldValues[field.variable];
        if (value === undefined || value === null || value === '') return;
        
        const numValue = parseFloat(value);
        if (!Number.isFinite(numValue)) return;
        
        // For optional non-override fields, check if the checkbox is enabled before including
        // This ensures unchecked optional fields (like ef_quantity) are not used in calculations
        const isOptionalField = !field.required && !field.isOverride;
        if (isOptionalField) {
          const overrideKey = `override_${field.variable}`;
          const isEnabled = dynamicFieldValues[overrideKey] || false;
          if (!isEnabled) return; // Skip this field if checkbox is not enabled
        }
        
        // Determine if this is a scope3-like flow
        const isBiogenicScope3 = formData.scope === 'biogenic' && biogenicScopeSelection === 'scope3';
        const isScope3Like = formData.scope === 'scope3' || isBiogenicScope3;
        
        // Unitless fields must never inherit a default display or fallback unit.
        let baseUnit = '';
        if (field.unitSource === 'none') {
          baseUnit = '';
        } else if (field.unitSource === 'fuel') {
          // For Scope 3 subcategory categories (C8, C10, C11, C13, C14), fallback to filteredScope3Activities
          if (isScope3Like && requiresSubcategory && !selectedFuel && scope3ActivityId) {
            baseUnit = dynamicFieldValues[`${field.variable}_unit`] || matchedActivityForEdit?.allowed_units?.[0] || matchedActivityForEdit?.default_unit || field.expectedUnit || 'kg';
          } else {
            baseUnit = dynamicFieldValues[`${field.variable}_unit`] || selectedFuel?.allowed_units?.[0] || field.expectedUnit;
          }
        } else if (field.unitSource === 'scope3_ef') {
          // Match the selector's default priority: the first allowed Scope 3 EF
          // unit is what users see before making an explicit choice.
          baseUnit = dynamicFieldValues[`${field.variable}_unit`] || matchedActivityForEdit?.allowed_units?.[0] || matchedActivityForEdit?.default_unit || field.expectedUnit || 'kg';
        } else if (field.unitSource === 'all_units') {
          baseUnit = dynamicFieldValues[`${field.variable}_unit`] || centralizedUnits[0]?.symbol || field.expectedUnit || '';
        } else {
          baseUnit = dynamicFieldValues[`${field.variable}_unit`] || field.allowedUnits?.[0] || field.expectedUnit || '';
        }
        
        // Apply compound suffix if field has compoundWithVariable
        let finalUnit = field.unitSource === 'none' ? '' : (baseUnit || 'kg');
        if (field.compoundWithVariable && finalUnit) {
          const linkedUnit = dynamicFieldValues[`${field.compoundWithVariable}_unit`];
          if (linkedUnit && typeof linkedUnit === 'string' && linkedUnit.trim()) {
            // Only add suffix if baseUnit doesn't already contain it
            if (!finalUnit.includes('/')) {
              finalUnit = `${finalUnit}/${linkedUnit.trim()}`;
            }
          }
        }
        
        // Track if we have any non-override field with a positive value
        // This replaces the hardcoded qty/activity_value check
        if (!field.isOverride && numValue > 0) {
          hasValidInput = true;
        }
        
        inputs[field.variable] = { value: numValue, unit: finalUnit };
      });

      const customFuelCalculation = editUseCustomFuel
        ? buildCustomFuelCalculationPayload({
          dynamicFieldValues,
          formData,
          categoryCode: editGhgFormContext.categoryCode,
          calculationMethodology: editCalcMethodology,
        })
        : null;
      if (customFuelCalculation) {
        Object.assign(inputs, customFuelCalculation.inputs);
      }
      
      // Determine if this is a scope3-like flow for calculation checks
      const isBiogenicScope3Calc = formData.scope === 'biogenic' && biogenicScopeSelection === 'scope3';
      const isScope3LikeCalc = formData.scope === 'scope3' || isBiogenicScope3Calc;
      
      // For Scope 3 (or biogenic scope3), require method and activity selection (or custom activity for supplier_basis)
      // For other scopes, require at least one valid input
      const canCalculate = isScope3LikeCalc 
        ? (scope3Method && (
            (scope3Method === 'supplier_basis' && useCustomActivity && scope3CustomActivity?.trim() && hasValidInput) ||
            (scope3Method === 'supplier_basis' && !useCustomActivity && scope3ActivityId && hasValidInput) ||
            (scope3Method !== 'supplier_basis' && scope3ActivityId && hasValidInput)
          ))
        : editUseCustomFuel ? customFuelCalculation?.isReady : hasValidInput;
      
      if (!canCalculate) {
        setBackendCalcResult(null);
        return;
      }
      
      // Build user overrides from override fields
      const userOverrides = {};
      dynamicInputFields.filter(f => f.isOverride).forEach(field => {
        const overrideKey = `override_${field.variable}`;
        if (dynamicFieldValues[overrideKey]) {
          const value = dynamicFieldValues[field.variable];
          if (value !== undefined && value !== null && value !== '') {
            const unit = dynamicFieldValues[`${field.variable}_unit`] || field.expectedUnit || '';
            userOverrides[field.variable] = { value: parseFloat(value), unit: unit };
          }
        }
      });
      if (customFuelCalculation) {
        Object.assign(userOverrides, customFuelCalculation.userOverrides);
      }
      const hasConfiguredDensityField = dynamicInputFields.some((field) => field.variable === 'density');
      if (!hasConfiguredDensityField && dynamicFieldValues.density !== undefined && dynamicFieldValues.density !== '') {
        const densityValue = parseFloat(dynamicFieldValues.density);
        if (Number.isFinite(densityValue) && densityValue > 0) {
          userOverrides.density = normalizeDensityForCalcEngine({
            value: densityValue,
            unit: normalizeCustomFuelDensityUnit(dynamicFieldValues.density_unit || 'kg/L'),
          });
        }
      }
      
      // Build decision inputs from maps_to_context
      const decisionInputs = buildEditDecisionInputs();
      
      // Determine effective scope for category lookup
      const isBiogenicScope3 = formData.scope === 'biogenic' && biogenicScopeSelection === 'scope3';
      const effectiveScope = isBiogenicScope3 ? 'scope3' : formData.scope;
      const isScope3Like = formData.scope === 'scope3' || isBiogenicScope3;
      
      // Find category ID
      const categoryObj = dynamicCategories.find(
        c => c.name === (formData.category || selectedCategory) && c.scope_code === effectiveScope
      );
      
      if (!categoryObj?.id) {
        setBackendCalcResult(null);
        return;
      }
      
      // Build scope3 context with default_unit for auto-conversion
      const matchedEFForPreview = filteredScope3Activities.find(a => a.id === scope3ActivityId);
      
      const scope3ContextPreview = isScope3Like ? {
        calculation_method_scope3: scope3Method,
        ...(scope3Method === 'spend_basis' && {
          spend_currency_conversion_method: spendCurrencyConversionMethod || 'standard',
        }),
        scope3_ef_id: scope3ActivityId,
        // For supplier_basis with custom activity, use the custom activity name
        activity: (scope3Method === 'supplier_basis' && useCustomActivity) 
          ? scope3CustomActivity 
          : matchedEFForPreview?.activity,
        scope3_ef_default_unit: matchedEFForPreview?.default_unit || '',
      } : {};
      
      // For Scope 3 subcategory categories (C8, C10, C11, C13, C14) with fugitive emissions,
      // use the activity name as fuel_name since the activity IS the fuel (e.g., "HFC-32")
      // Skip this for supplier_basis as it uses a basic formula without fuel_database lookup
      let fuelNameForContext = editUseCustomFuel ? editCustomFuelName : selectedFuel?.fuel_name;
      
      if (isScope3Like && requiresSubcategory && scope3Method !== 'supplier_basis' && scope3Subcategory === 'fugitive_emissions' && matchedEFForPreview?.activity) {
        fuelNameForContext = matchedEFForPreview.activity;
      }
      
      // For custom fuel, merge ef_quantity from inputs into user_overrides
      // so the backend property resolver can use it
      if (editUseCustomFuel) {
        if (inputs.ef_quantity) {
          userOverrides.ef_quantity = inputs.ef_quantity;
          userOverrides.emission_factor = inputs.ef_quantity;
        }
      }
      
      // Call backend calc engine with dynamic inputs
      const payload = {
        category_id: categoryObj.id,
        decision_inputs: decisionInputs,
        inputs: inputs,
        context: {
          fuel_name: fuelNameForContext,
          fuel_id: editUseCustomFuel ? null : selectedFuel?.id,
          scope: effectiveScope, // Use effective scope for context
          category: formData.category || selectedCategory,
          reporting_period: formData.reporting_period_start, // For currency conversion year lookup
          reporting_year: reportingYearFromPeriod,
          is_custom_fuel: editUseCustomFuel || false,
          // Scope 3 specific context
          ...scope3ContextPreview,
        },
        user_overrides: userOverrides,
        dry_run: true,
        // Pass scope3_ef_id at top level for backend to lookup fuel_database (fugitive emissions)
        ...(isScope3Like && scope3ActivityId && { scope3_ef_id: scope3ActivityId }),
      };
      
      // Use the calculator hook (handles debouncing and API call)
      triggerCalcEngine(payload);
      return;
    }
    
    // FALLBACK: Legacy behavior when no dynamic fields loaded
    const quantity = parseFloat(formData.quantity);
    if (!quantity || quantity <= 0) {
      setBackendCalcResult(null);
      return;
    }
    
    // Build overrides object - pass custom values when override is enabled
    const overrides = {
      override_calorific_value: overrideCalorificValue,
      calorific_value: overrideCalorificValue ? formData.calorific_value : null,
      override_density: overrideDensity,
      density: overrideDensity ? formData.density : null,
      override_emission_factor_heat: overrideEmissionFactorHeat,
      emission_factor_heat: overrideEmissionFactorHeat ? formData.emission_factor_heat : null
    };
    
    // Call the backend calc engine (uses its own debouncing)
    setCalculationError('');
    executeBackendCalc({
      scope: formData.scope,
      category: formData.category || selectedCategory,
      fuel: selectedFuel,
      quantity: quantity,
      unit: formData.quantity_unit || 'kg',
      overrides: overrides,
      gwpConfig: gwpConfig,
      dryRun: true,
      calculationMethodology: editCalcMethodology,
      decisionTreeVersionId: null,
      formulaVersionId: null,
    }).then(result => {
      if (result) {
        setBackendCalcResult(result);
        setCalcEngineUsed(true);
      } else {
        setBackendCalcResult(null);
        setCalcEngineUsed(false);
        setCalculationError('Calculation unavailable. Please review the entered data and try again.');
      }
    }).catch(error => {
      console.error('[CalcEngine] Backend calculation error:', error);
      setBackendCalcResult(null);
      setCalcEngineUsed(false);
      setCalculationError("We couldn't complete this calculation. Please review the entered data and try again.");
    });
  }, [
    dialogOpen, editingEmission, isFormDirty, selectedFuel?.id, formData.quantity, formData.quantity_unit,
    formData.scope, formData.category, selectedCategory, formData.calorific_value,
    formData.density, formData.emission_factor_heat, overrideCalorificValue,
    overrideDensity, overrideEmissionFactorHeat, dynamicInputFields, dynamicFieldValues,
    dynamicCategories, buildEditDecisionInputs, getAuthHeader,
    scope3Method, spendCurrencyConversionMethod, scope3ActivityId, filteredScope3Activities,
    useCustomActivity, scope3CustomActivity, scope3Subcategory, typeOfProduct, biogenicScopeSelection, editDraft.allocationMethod,
    editCalcMethodology, editUseCustomFuel, editCustomFuelName, editProcessType, editCapabilities.requiresFuel,
    isOriginalEditSelection, isEditC7EmployeeCommuting, clearCalcResult,
  ]);
  
  // Use backend calculation engine result exclusively
  const effectiveCalculatedEmissions = useMemo(() => {
    if (liveCalculationValidationError) return null;
    // Use backend result if available
    if (backendCalcResult && useBackendCalc) {
      return {
        ...backendCalcResult,
        auditLog: backendCalcResult.auditLog || emissionAuditLog
      };
    }
    
    // Show persisted emission values from the record itself.
    // The audit log enriches the trace panel but is not required for the
    // summary numbers — records created before the audit-link fix will
    // still display their saved CO₂/CH₄/N₂O/CO₂e immediately.
    if (editingEmission && !isFormDirty) {
      return {
        auditLog: emissionAuditLog,
        co2Emissions: editingEmission.co2_emissions,
        ch4Emissions: editingEmission.ch4_emissions,
        n2oEmissions: editingEmission.n2o_emissions,
        co2eEmissions: editingEmission.co2e_emissions,
        co2OutputUnit: 'tCO₂',
        ch4OutputUnit: 'tCH₄',
        n2oOutputUnit: 'tN₂O',
        co2eOutputUnit: 'tCO₂e',
        appliedFormulaName: editingEmission.formula_name,
        formulaId: editingEmission.formula_id,
        formulaVersionId: editingEmission.formula_version_id,
        decisionTreeVersionId: editingEmission.decision_tree_version_id,
      };
    }
    
    return null;
  }, [backendCalcResult, useBackendCalc, emissionAuditLog, editingEmission, isFormDirty, liveCalculationValidationError]);

  // Track calculation state - set isCalculating true when inputs change, false after a short delay
  // This ensures the Save button is disabled while calculations are updating
  useEffect(() => {
    // Only track when edit dialog is open
    if (!editingEmission) return;
    
    setIsCalculating(true);
    const timer = setTimeout(() => {
      setIsCalculating(false);
    }, 300); // 300ms debounce to allow useMemo to complete
    
    return () => clearTimeout(timer);
  }, [formData.quantity, formData.calorific_value, formData.density, formData.emission_factor_heat,
      formData.calorific_value, overrideCalorificValue, overrideDensity, overrideEmissionFactorHeat,
      editingEmission]);

  // E2: handleFileUpload, handleDeleteExistingEvidence, handleDeleteAllEvidences,
  // handleRemoveEvidence — moved to useEvidenceManagement hook.

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (calculationError && !isEditC7EmployeeCommuting) {
      toast.error(`Nothing was saved. ${calculationError}`);
      return;
    }

    const missingRequiredQuantityField = !isEditC7EmployeeCommuting && dynamicInputFields.find((field) => {
      if (!field.required || field.isOverride || field.presentationOnly) return false;
      const identity = `${field.variable || ''} ${field.fieldKey || ''} ${field.label || ''}`.toLowerCase();
      if (!/quantity|\bqty\b|activity_value|consum|energy|volume|mass|distance|travelled/.test(identity)) return false;
      const rawValue = dynamicFieldValues?.[field.variable] ?? dynamicFieldValues?.[field.fieldKey];
      const value = typeof rawValue === 'object' ? rawValue?.value : rawValue;
      return value === '' || value === null || value === undefined;
    });
    if (missingRequiredQuantityField) {
      const label = typeof missingRequiredQuantityField.label === 'object'
        ? missingRequiredQuantityField.label.value
        : (missingRequiredQuantityField.label || 'Quantity Used');
      toast.error(`${label} missing.`);
      return;
    }

    // E3: persistCalcAuditLog moved to ./emissions/utils/persistCalcAuditLog.
    // Thin wrapper binds local state for the dispatch branches below.
    const persistCalcAuditLogLocal = (emissionId) => persistCalcAuditLogShared(emissionId, {
      formData,
      biogenicScopeSelection,
      dynamicCategories,
      selectedCategory,
      dynamicInputFields,
      dynamicFieldValues,
      buildEditDecisionInputs,
      filteredScope3Activities,
      scope3ActivityId,
      scope3Method,
      spendCurrencyConversionMethod,
      scope3Subcategory,
      useCustomActivity,
      scope3CustomActivity,
      requiresSubcategory,
      selectedFuel,
      editUseCustomFuel,
      editCustomFuelName,
      editCalcMethodology,
      editingEmission,
      preserveOriginalVersion: isOriginalEditSelection,
      getAuthHeader,
    });
    
    // C7 EMPLOYEE COMMUTING - Always uses multi-employee mode
    // Business logic (validation + payload construction) lives in the C7 category module.
    // UI rendering stays in Emissions.js via MultiEmployeeInput.
    if (isEditC7EmployeeCommuting) {
      const c7Module = categoryRegistry.get('c7');
      if (!c7Module || !c7Module.validateEditSubmission || !c7Module.buildEditPayload) {
        toast.error('C7 module not initialised. Please reload the page.');
        return;
      }

      setIsSaving(true);
      const calculation = await calculateC7EditEmployeesForSave();
      if (calculation?.error) {
        toast.error(`Nothing was saved. ${calculation.error}`);
        setIsSaving(false);
        return;
      }
      const employeesForSave = calculation.employees;

      // 1. Validate via module
      const validation = c7Module.validateEditSubmission({
        editEmployees: employeesForSave,
        editingEmission,
        processNames: formData.process_names,
      });
      if (!validation.valid) {
        toast.error(validation.errorMessage);
        setIsSaving(false);
        return;
      }

      // 2. Build payload via module
      const builtPayload = c7Module.buildEditPayload({
        formData,
        editingEmission,
        editEmployees: employeesForSave,
        scope3Method,
        spendCurrencyConversionMethod,
        allocationMethod: editDraft.allocationMethod,
        scope3ActivityId,
        scope3ActivityType,
        scope3CustomActivity,
        useCustomActivity,
        filteredScope3Activities,
        editEmployeeMonthlyTotals: calculation.monthlyTotals,
        editEmployeeYearlyTotal: calculation.yearlyTotal,
        validProcessNames: validation.validProcessNames,
        preserveOriginalVersion: isOriginalEditSelection,
        categoryId: editGhgFormContext.categoryId,
      });
      const totalCo2e = builtPayload.__totalCo2e;
      // Strip orchestration-only field before sending
      const payload = { ...builtPayload };
      delete payload.__totalCo2e;

      try {
        const response = await axios.put(`${API}/emissions/${editingEmission.id}`, payload, {
          headers: getAuthHeader()
        });
        
        if (response.data) {
          await commitC7EvidenceChanges(editingEmission.id, employeesForSave);
          toast.success(`Updated ${employeesForSave.length} employee commuting records (${totalCo2e.toFixed(4)} tCO2e total)`);
          // NOTE: Audit log persistence (POST /calc-engine/execute-by-category)
          // is intentionally skipped for C7. The calc-engine endpoint expects
          // aggregated `dynamicFieldValues`-based inputs; C7's per-employee
          // per-month inputs don't match that contract and return HTTP 400.
          // The legacy code also did NOT persist the audit log for C7.
          setDialogOpen(false);
          resetForm();
          fetchData(); // Refresh the emissions list
        }
      } catch (error) {
        toast.error(getEmissionUpdateErrorMessage(error));
      } finally {
        setIsSaving(false);
      }
      return;
    }
    
    // FLAT-FIELD SCOPE 3 + SCOPE 1 CATEGORIES — module-owned validation +
    // payload (config-driven path). Applies to any module exposing
    // `buildEditPayload` EXCEPT C7 (which has its own multi-employee
    // branch above). Scope 2 still uses the legacy path until migrated.
    if (
      activeCategoryModule?.buildEditPayload &&
      activeCategoryModule?.id !== 'c7'
    ) {
      // Read override DOM flags for Scope 1 (no-op for Scope 3 paths)
      const cvCheckbox = document.querySelector('[data-testid="override-calorific-checkbox"]');
      const densityCheckbox = document.querySelector('[data-testid="override-density-checkbox"]');
      const isOverrideCV = cvCheckbox?.checked || false;
      const isOverrideDensity = densityCheckbox?.checked || false;

      const validation = activeCategoryModule.validateEditSubmission({
        // Scope 3 props
        scope3Method,
        spendCurrencyConversionMethod,
        allocationMethod: editDraft.allocationMethod,
        scope3ActivityId,
        scope3CustomActivity,
        useCustomActivity,
        // Common props
        dynamicInputFields,
        dynamicFieldValues,
        processNames: formData.process_names,
        effectiveCalculatedEmissions,
        formData,
        frequencyType: editFrequencyType,
        categoryCode: editGhgFormContext.categoryCode,
        capabilities: editCapabilities,
        // Scope 1 props
        isOverrideCV,
        isOverrideDensity,
        overrideCalorificValue,
        overrideDensity,
        overrideEmissionFactorHeat,
        overrideJustification,
        // Custom fuel props
        editUseCustomFuel,
        editCustomFuelName,
        editCalcMethodology,
        editProcessType,
      });
      if (!validation.valid) {
        toast.error(validation.errorMessage);
        return;
      }

      try {
        const payload = activeCategoryModule.buildEditPayload({
          // Scope 3 props
          scope3Method,
          spendCurrencyConversionMethod,
          allocationMethod: editDraft.allocationMethod,
          scope3ActivityId,
          scope3ActivityType,
          scope3Subcategory,
          typeOfProduct,
          scope3CustomActivity,
          useCustomActivity,
          // Common
          formData,
          categoryCode: editGhgFormContext.categoryCode,
          capabilities: editCapabilities,
          editingEmission,
          biogenicScopeSelection,
          dynamicInputFields,
          dynamicFieldValues,
          effectiveCalculatedEmissions,
          preserveOriginalVersion: isOriginalEditSelection,
          selectedFuel,
          filteredScope3Activities,
          centralizedUnits,
          // Scope 1 override flags
          isOverrideCV,
          isOverrideDensity,
          overrideEmissionFactorHeat,
          overrideJustification,
          // Custom fuel props
          editUseCustomFuel,
          editCustomFuelName,
          editCalcMethodology,
          editProcessType,
        });

        setIsSaving(true);
        const response = await axios.put(`${API}/emissions/${editingEmission.id}`, payload, {
          headers: getAuthHeader()
        });
        if (response.data) {
          await commitEvidenceChanges(editingEmission.id);
          toast.success('Emission updated successfully');
          // Persist calc audit log so override sources reload correctly on re-edit
          await persistCalcAuditLogLocal(editingEmission.id);
          setDialogOpen(false);
          resetForm();
          fetchData();
        }
      } catch (error) {
        toast.error(getEmissionUpdateErrorMessage(error));
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // No module matched — defensive fallback.
    // With Scope 1/2/3 + biogenic all routed through module dispatch above,
    // this branch should be unreachable for any valid emission record.
    // If it fires, it indicates an unregistered category or a stale dialog.
    toast.error('No category module matched for this emission. Cannot save.');
    console.error('[Emissions.handleSubmit] No active category module for', {
      scope: formData.scope,
      category: formData.category,
      biogenicScopeSelection,
    });
  };

  // E4: handleEdit body extracted to ./emissions/utils/editEmissionDispatch.js.
  const handleEdit = (emission) => {
    setLiveCalculationValidationError('');
    clearCalcResult();
    setBackendCalcResult(null);
    setHasChangedCalculationSelection(false);
    return editEmissionDispatchShared(emission, {
      // State reads
      scope3EFData, fugitiveEmissionsData, fuelDatabase,
      // Setters
      setEditDraft, setEditingEmissionId,
      setEmissionAuditLog, setIsEditLoading, setDialogOpen, setIsFormDirty,
      setEditingEmission, activeEditIdRef,
    });
  };

  // Deep-link from /ghg/approvals: open the edit dialog for ?edit=<id> once
  // the emissions list is loaded. Strips the param after firing so a refresh
  // doesn't re-trigger. If the record isn't in the loaded list (e.g. admin
  // view filters out pending records), falls back to fetching it directly.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editId = params.get('edit');
    if (!editId) return;
    if (loading) return;

    const target = (emissions || []).find((e) => e.id === editId);
    const stripParam = () => {
      params.delete('edit');
      navigate(
        { pathname: location.pathname, search: params.toString() },
        { replace: true }
      );
    };

    if (target) {
      handleEdit(target);
      stripParam();
      return;
    }

    // Fallback: fetch single record directly (covers admin opening pending
    // records from the Approvals page, which are excluded from /api/emissions).
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${API}/emissions/${editId}`, {
          headers: getAuthHeader(),
        });
        if (cancelled) return;
        if (resp?.data) {
          handleEdit(resp.data);
          stripParam();
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(err?.response?.data?.detail || 'Failed to load record');
          stripParam();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.search, emissions, loading]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/emissions/${id}`, {
        headers: getAuthHeader()
      });
      toast.success('Emission record deleted successfully');
      setDeleteConfirmOpen(false);
      setEmissionToDelete(null);      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  // Bulk delete state and handler
  const [bulkDeleteIds, setBulkDeleteIds] = useState([]);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);

  const openBulkDeleteConfirm = (ids) => {
    setBulkDeleteIds(ids);
    setBulkDeleteConfirmOpen(true);
  };

  const handleBulkDelete = async () => {
    if (bulkDeleteIds.length === 0) return;
    
    try {
      // Track results for each delete request
      let deletedCount = 0;
      let queuedForApprovalCount = 0;
      let failedCount = 0;
      
      // Process each delete individually to handle approval workflow responses
      for (const id of bulkDeleteIds) {
        try {
          const response = await axios.delete(`${API}/emissions/${id}`, { headers: getAuthHeader() });
          // Check if queued for approval vs direct delete
          if (response.data?.message?.toLowerCase().includes('submitted for approval')) {
            queuedForApprovalCount++;
          } else {
            deletedCount++;
          }
        } catch (error) {
          // 403 means blocked - not authorized or requires approval and blocked
          if (error.response?.status === 403) {
            failedCount++;
          } else {
            failedCount++;
          }
        }
      }
      
      // Show appropriate toast messages based on results
      if (deletedCount > 0) {
        toast.success(`${deletedCount} record(s) deleted successfully`);
      }
      if (queuedForApprovalCount > 0) {
        toast.info(`${queuedForApprovalCount} record(s) submitted for approval`);
      }
      if (failedCount > 0) {
        toast.error(`${failedCount} record(s) could not be deleted`);
      }
      
      setBulkDeleteConfirmOpen(false);
      setBulkDeleteIds([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk delete failed');
    }
  };

  const openDeleteConfirm = (emission) => {
    setEmissionToDelete(emission);
    setDeleteConfirmOpen(true);
  };

  const resetForm = () => {
    activeEditIdRef.current = null;
    discardEvidenceChanges();
    setEditingEmission(null);
    setEditDraft(createEmptyEmissionDraft(activeScope));
    setEditFormConfig(null); // Clear form config
    setUploadedEvidence(null);
    setLiveCalculationValidationError('');
    clearCalcResult();
    setBackendCalcResult(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsFormDirty(false);
    setIsEditLoading(false);
    setDialogOpen(true);
  };

  // Handle dialog change with unsaved changes protection (#19)
  const handleDialogChange = (open) => {
    if (!open && isFormDirty) {
      // User is trying to close with unsaved changes - show confirmation
      setShowUnsavedChangesDialog(true);
      setPendingCloseAction('close');
      return;
    }
    setDialogOpen(open);
    if (!open) {
      resetForm();
      setIsFormDirty(false);
      setIsEditLoading(false); // Reset edit loading state when dialog closes
    }
  };
  
  // Handle outside click on modal - prevent closing (#19)
  const handleInteractOutside = (e) => {
    e.preventDefault(); // Prevent closing on outside click
  };
  
  // Handle ESC key - show confirmation if dirty (#19)
  const handleEscapeKeyDown = (e) => {
    if (isFormDirty) {
      e.preventDefault();
      setShowUnsavedChangesDialog(true);
      setPendingCloseAction('close');
    }
  };
  
  // Confirm discard changes (#19)
  const handleDiscardChanges = () => {
    setShowUnsavedChangesDialog(false);
    setIsFormDirty(false);
    setIsEditLoading(false); // Reset edit loading state
    setDialogOpen(false);
    resetForm();
  };
  
  // Continue editing (#19)
  const handleContinueEditing = () => {
    setShowUnsavedChangesDialog(false);
    setPendingCloseAction(null);
  };
  
  // Mark form as dirty when any input changes
  const markFormDirty = useCallback(() => {
    if (!isFormDirty) {
      setIsFormDirty(true);
    }
  }, [isFormDirty]);

  // Wrapper to update form data and mark as dirty
  const updateFormData = useCallback((updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
    markFormDirty();
  }, [markFormDirty]);

  // Handler for calculating emissions for a specific employee and month in edit mode
  const handleCalculateEditEmployeeMonth = useCallback(async (employeeId, monthKey, employee) => {
    setIsCalculatingEditEmployee(true);
    try {
      // Check if this is yearly mode (monthKey === 'yearly')
      const isYearlyMode = monthKey === 'yearly';
      
      // Get input data based on mode - yearly uses yearly_data, monthly uses monthly_data
      const inputData = isYearlyMode 
        ? employee.yearly_data 
        : employee.monthly_data?.[monthKey];
      
      if (!inputData?.inputs || Object.keys(inputData.inputs).length === 0) {
        toast.error('Please enter input values first');
        setIsCalculatingEditEmployee(false);
        return;
      }

      // Check if all inputs have values
      const hasValidInputs = Object.values(inputData.inputs).some(v => v !== '' && v !== null && v !== undefined);
      if (!hasValidInputs) {
        toast.error('Please enter at least one input value');
        setIsCalculatingEditEmployee(false);
        return;
      }

      // Validate required fields
      if (!scope3Method) {
        toast.error('Please select a calculation method first');
        setIsCalculatingEditEmployee(false);
        return;
      }

      if (!scope3ActivityType) {
        toast.error('Please select an activity type first');
        setIsCalculatingEditEmployee(false);
        return;
      }

      // Find the matched activity from scope3 EF data (#6 - Fix: use scope3ActivityId first, then fallback to activity_type)
      const activityType = scope3ActivityType;
      
      // Normalize activity type for matching (handle both 'wfh' and 'Work From Home' formats)
      const normalizeActivityType = (type) => {
        if (!type) return '';
        return type.toLowerCase().replace(/\s+/g, '_');
      };
      const normalizedActivityType = normalizeActivityType(activityType);
      
      // For supplier_basis with custom activity, skip activity ID requirement
      if (!useCustomActivity && !scope3ActivityId) {
        // Priority: 1) Selected scope3ActivityId, 2) First match for activity_type
        let matchedActivity = filteredScope3Activities.find(a => 
          a.activity_type === activityType || 
          normalizeActivityType(a.activity_type) === normalizedActivityType
        );
        
        if (!matchedActivity) {
          toast.error('Please select a specific activity from the dropdown or use custom activity');
          setIsCalculatingEditEmployee(false);
          return;
        }
      }
      
      // Find matched activity (may be null for custom activity)
      let matchedActivity = null;
      if (!useCustomActivity) {
        if (scope3ActivityId) {
          matchedActivity = filteredScope3Activities.find(a => a.id === scope3ActivityId);
        }
        
        // Fallback to activity_type match if no specific activity selected
        if (!matchedActivity) {
          matchedActivity = filteredScope3Activities.find(a => 
            a.activity_type === activityType || 
            normalizeActivityType(a.activity_type) === normalizedActivityType
          );
        }

        if (!matchedActivity) {
          toast.error(`Activity "${activityType}" not found. Please select a valid activity.`);
          setIsCalculatingEditEmployee(false);
          return;
        }
      }
      
      // Use the matched activity's emission factor for supplier_basis if no custom EF provided (or null for custom activity)
      const efFromActivity = matchedActivity?.emission_factor || null;
      const efUnitFromActivity = matchedActivity?.ef_unit || null;

      // Build decision_inputs for decision tree traversal
      const decisionInputs = {
        calculation_method_scope3: scope3Method,
        activity_type: normalizedActivityType || activityType,
      };

      // Build inputs for formula execution (use inputData which handles both yearly and monthly)
      const formulaInputs = {};
      Object.entries(inputData.inputs).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          const fieldConfig = dynamicInputFields.find(f => f.variable === key);
          formulaInputs[key] = {
            value: parseFloat(value),
            unit: fieldConfig?.expectedUnit || fieldConfig?.unit || ''
          };
        }
      });

      // Get category object
      const categoryObj = dynamicCategories.find(c => 
        c.name === formData.category && c.scope_code === 'scope3'
      );

      if (!categoryObj) {
        toast.error('Category not found');
        setIsCalculatingEditEmployee(false);
        return;
      }

      const payload = {
        category_id: categoryObj.id,
        decision_inputs: decisionInputs,
        inputs: formulaInputs,
        context: {
          ...decisionInputs,
          reporting_period: formData.reporting_period_start, // For currency conversion year lookup
          activity: matchedActivity?.activity || scope3CustomActivity || 'Custom Activity', // For emission factor lookup
          fuel_name: matchedActivity?.activity || scope3CustomActivity || 'Custom Activity', // Alias for property source mapping
          scope3_ef_id: matchedActivity?.id || null,
          use_custom_activity: useCustomActivity,
        },
        scope3_ef_id: matchedActivity?.id || null,
      };

      // Call calc engine
      const response = await axios.post(
        `${API}/calc-engine/execute-by-category`,
        payload,
        { headers: getAuthHeader() }
      );

      if (response.data?.outputs) {
        const co2e = response.data.outputs.co2e?.value || 0;
        
        // Store audit log for calculation ledger display
        const auditLog = response.data.audit_log || [];
        const appliedFactors = response.data.applied_factors || {};
        
        // Build emissions and calculation_details objects
        const emissionsData = {
          co2: response.data.outputs.co2?.value || 0,
          ch4: response.data.outputs.ch4?.value || 0,
          n2o: response.data.outputs.n2o?.value || 0,
          co2e: co2e,
        };
        const calculationDetails = {
          audit_log: auditLog,
          applied_factors: appliedFactors,
          formula_id: response.data.resolved_formula?.id || null,
          formula_version_id: response.data.resolved_formula?.version_id || null,
          decision_tree_version_id: response.data.resolved_decision_tree?.version_id || null,
          formula_name: response.data.resolved_formula?.name || '',
          outputs: response.data.outputs,
        };
        
        // Update employee with calculated emissions and audit data
        setEditEmployees(prevEmployees => {
          const updatedEmployees = prevEmployees.map(emp => {
            if (emp.id === employeeId) {
              // Handle yearly mode vs monthly mode
              if (isYearlyMode) {
                const existingYearlyData = emp.yearly_data || { inputs: {} };
                return {
                  ...emp,
                  yearly_data: {
                    ...existingYearlyData,
                    emissions: emissionsData,
                    calculation_details: calculationDetails,
                  },
                };
              } else {
                // Monthly mode
                const existingMonthData = emp.monthly_data?.[monthKey] || { inputs: {} };
                return {
                  ...emp,
                  monthly_data: {
                    ...emp.monthly_data,
                    [monthKey]: {
                      ...existingMonthData,
                      emissions: emissionsData,
                      calculation_details: calculationDetails,
                    },
                  },
                };
              }
            }
            return emp;
          });
          
          // Recalculate totals based on mode
          if (isYearlyMode) {
            // For yearly mode, sum all employee yearly emissions
            let yearlyTotalValue = 0;
            updatedEmployees.forEach(emp => {
              yearlyTotalValue += emp.yearly_data?.emissions?.co2e || 0;
            });
            setEditEmployeeYearlyTotal({ co2e: yearlyTotalValue });
            setEditEmployeeMonthlyTotals({});
          } else {
            // For monthly mode, calculate monthly and yearly totals
            const newMonthlyTotals = {};
            const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
            monthKeys.forEach(mk => {
              let total = 0;
              updatedEmployees.forEach(emp => {
                total += emp.monthly_data?.[mk]?.emissions?.co2e || 0;
              });
              if (total > 0) {
                newMonthlyTotals[mk] = { co2e: total };
              }
            });
            setEditEmployeeMonthlyTotals(newMonthlyTotals);
            
            // Calculate yearly total from monthly
            const yearlyTotalValue = Object.values(newMonthlyTotals).reduce((sum, m) => sum + (m.co2e || 0), 0);
            setEditEmployeeYearlyTotal({ co2e: yearlyTotalValue });
          }
          
          return updatedEmployees;
        });
        
        toast.success(`Calculated: ${co2e.toFixed(4)} tCO2e`);
      } else {
        toast.error('No calculation results returned');
      }
    } catch (error) {
      console.error('[Edit MultiEmployee Calc] Error:', error);
      console.error('[Edit MultiEmployee Calc] Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to calculate emissions');
    } finally {
      setIsCalculatingEditEmployee(false);
    }
  }, [scope3Method, scope3ActivityType, scope3ActivityId, filteredScope3Activities, dynamicCategories, formData.category, dynamicInputFields, getAuthHeader, useCustomActivity, scope3CustomActivity]);

  const calculateC7EditEmployeesForSave = useCallback(async () => {
    const fail = (message) => ({ error: message });
    if (!scope3Method) return fail('Please select a calculation method first');
    if (!scope3ActivityType) return fail('Please select an activity type first');
    if (!useCustomActivity && !scope3ActivityId) {
      return fail('Please select a specific activity from the dropdown');
    }

    const normalizeActivityType = (value) => (
      value ? value.toLowerCase().replace(/\s+/g, '_') : ''
    );
    const normalizedActivityType = normalizeActivityType(scope3ActivityType);
    const matchedActivity = useCustomActivity
      ? null
      : filteredScope3Activities.find((activity) => activity.id === scope3ActivityId)
        || filteredScope3Activities.find((activity) => (
          activity.activity_type === scope3ActivityType
          || normalizeActivityType(activity.activity_type) === normalizedActivityType
        ));
    if (!useCustomActivity && !matchedActivity) {
      return fail('Activity not found. Please select a valid activity from the dropdown.');
    }
    const categoryObj = dynamicCategories.find((item) => (
      item.name === formData.category && item.scope_code === 'scope3'
    ));
    if (!categoryObj) return fail('Category not found');

    setIsCalculatingEditEmployee(true);
    try {
      const calculatedEmployees = editEmployees.map((employee) => ({ ...employee }));
      for (let employeeIndex = 0; employeeIndex < calculatedEmployees.length; employeeIndex += 1) {
        let employee = calculatedEmployees[employeeIndex];
        if (!employee.name?.trim()) return fail('Employee Name is required');
        const isYearly = editingEmission?.frequency_type === 'yearly';
        const periods = isYearly
          ? ['yearly']
          : Object.entries(employee.monthly_data || {})
            .filter(([, monthData]) => dynamicInputFields.some((field) => {
              const value = monthData?.inputs?.[field.variable];
              return value !== '' && value !== null && value !== undefined;
            }))
            .map(([monthKey]) => monthKey);
        if (periods.length === 0) return fail(`${employee.name}: enter at least one input value`);

        for (const periodKey of periods) {
          const inputData = isYearly ? employee.yearly_data : employee.monthly_data?.[periodKey];
          const inputs = inputData?.inputs || {};
          for (const field of dynamicInputFields.filter((item) => item.required && !item.isOverride)) {
            const value = inputs[field.variable];
            if (value === '' || value === null || value === undefined) {
              return fail(`${employee.name}: ${field.label} is required`);
            }
          }

          const formulaInputs = {};
          for (const field of dynamicInputFields) {
            const value = inputs[field.variable];
            if (value === '' || value === null || value === undefined) continue;
            const unit = inputs[`${field.variable}_unit`] || field.expectedUnit || field.unit || '';
            if (scope3Method === 'supplier_basis' && field.unitSource !== 'none' && !String(unit).trim()) {
              return fail(`${employee.name}: Unit is required for ${field.label}`);
            }
            formulaInputs[field.variable] = { value: parseFloat(value), unit };
          }

          let response;
          try {
            response = await axios.post(`${API}/calc-engine/execute-by-category`, {
              category_id: categoryObj.id,
              decision_inputs: {
                calculation_method_scope3: scope3Method,
                activity_type: normalizedActivityType || scope3ActivityType,
              },
              inputs: formulaInputs,
              context: {
                calculation_method_scope3: scope3Method,
                activity_type: normalizedActivityType || scope3ActivityType,
                reporting_period: editingEmission.reporting_period || formData.reporting_period_start,
                activity: matchedActivity?.activity || scope3CustomActivity || 'Custom Activity',
                fuel_name: matchedActivity?.activity || scope3CustomActivity || 'Custom Activity',
                scope3_ef_id: matchedActivity?.id || null,
                use_custom_activity: useCustomActivity,
              },
              scope3_ef_id: matchedActivity?.id || null,
            }, { headers: getAuthHeader() });
          } catch (error) {
            const detail = error.response?.data?.detail;
            return fail(`${employee.name}: ${typeof detail === 'string' ? detail : 'Failed to calculate emissions'}`);
          }
          if (!response.data?.outputs) return fail(`${employee.name}: No calculation results returned`);

          const emissions = {
            co2: response.data.outputs.co2?.value ?? 0,
            ch4: response.data.outputs.ch4?.value ?? 0,
            n2o: response.data.outputs.n2o?.value ?? 0,
            co2e: response.data.outputs.co2e?.value ?? 0,
          };
          const calculationDetails = {
            audit_log: response.data.audit_log || [],
            applied_factors: response.data.applied_factors || {},
            formula_id: response.data.resolved_formula?.id || null,
            formula_version_id: response.data.resolved_formula?.version_id || null,
            decision_tree_version_id: response.data.resolved_decision_tree?.version_id || null,
            formula_name: response.data.resolved_formula?.name || '',
            outputs: response.data.outputs,
          };
          employee = isYearly ? {
            ...employee,
            yearly_data: {
              ...employee.yearly_data,
              emissions,
              calculation_details: calculationDetails,
            },
          } : {
            ...employee,
            monthly_data: {
              ...employee.monthly_data,
              [periodKey]: {
                ...employee.monthly_data[periodKey],
                emissions,
                calculation_details: calculationDetails,
              },
            },
          };
          calculatedEmployees[employeeIndex] = employee;
        }
      }

      const monthlyTotals = {};
      calculatedEmployees.forEach((employee) => {
        Object.entries(employee.monthly_data || {}).forEach(([monthKey, monthData]) => {
          if (monthData?.emissions?.co2e === null || monthData?.emissions?.co2e === undefined) return;
          monthlyTotals[monthKey] = {
            co2e: (monthlyTotals[monthKey]?.co2e || 0) + monthData.emissions.co2e,
          };
        });
      });
      const yearlyTotal = {
        co2e: editingEmission?.frequency_type === 'yearly'
          ? calculatedEmployees.reduce((total, employee) => (
            total + (employee.yearly_data?.emissions?.co2e || 0)
          ), 0)
          : Object.values(monthlyTotals).reduce((total, month) => total + (month.co2e || 0), 0),
      };
      setEditEmployees(calculatedEmployees);
      setEditEmployeeMonthlyTotals(monthlyTotals);
      setEditEmployeeYearlyTotal(yearlyTotal);
      return { employees: calculatedEmployees, monthlyTotals, yearlyTotal };
    } finally {
      setIsCalculatingEditEmployee(false);
    }
  }, [dynamicCategories, dynamicInputFields, editEmployees, editingEmission, filteredScope3Activities, formData.category, formData.reporting_period_start, getAuthHeader, scope3ActivityId, scope3ActivityType, scope3CustomActivity, scope3Method, useCustomActivity]);

  // Get unique categories from emissions for filtering
  const getCategories = useMemo(() => {
    const categories = {};
    fuelDatabase
      .filter(f => f.scope === formData.scope)
      .forEach(f => {
        if (!categories[f.category]) {
          categories[f.category] = {};
        }
        categories[f.category][f.fuel_name] = f;
      });
    return categories;
  }, [formData.scope, fuelDatabase]);

  // Check if organization has scope 3 access - needed for filtering biogenic scope3 records
  const hasScope3Access = useMemo(() => {
    const enabledAccess = organization?.enabled_access;
    if (isSupplierUser) return false;
    return enabledAccess?.includes('scope1_2_3') || false;
  }, [isSupplierUser, organization]);

  // KPI Assignment-based scope access check (combines org-level and assignment-level)
  const canAccessScopeWithKPI = useCallback((scope) => {
    // Admins always have full access
    if (user?.role === 'admin' || user?.role === 'super_admin') return true;
    // Check KPI assignment access
    return kpiCanAccessScope(scope);
  }, [user?.role, kpiCanAccessScope]);

  // Filter facilities based on KPI assignment for current scope
  const kpiFilteredFacilities = useMemo(() => {
    if (user?.role === 'admin' || user?.role === 'super_admin') return facilities;
    return filterFacilitiesByScope(facilities, activeScope);
  }, [facilities, activeScope, filterFacilitiesByScope, user?.role]);

  // Apply filters
  // Get active facilities only for filtering emissions
  const activeFacilityIds = useMemo(() => {
    return facilities.filter(f => f.is_active !== false).map(f => f.id);
  }, [facilities]);

  // Get KPI-allowed facility IDs for current scope
  const kpiAllowedFacilityIds = useMemo(() => {
    if (user?.role === 'admin' || user?.role === 'super_admin') return null; // null means all allowed
    return kpiFilteredFacilities.map(f => f.id);
  }, [kpiFilteredFacilities, user?.role]);

  const filteredEmissions = useMemo(() => {
    let filtered = emissions.filter(e => {
      // Supplier users see the current resubmission revision only. The prior
      // immutable revision remains stored for audit history but is not a live
      // GHG log entry.
      if (isSupplierUser && e.source === 'supplier' && e.is_current_revision === false) {
        return false;
      }

      // Hide emissions from deactivated facilities
      if (!activeFacilityIds.includes(e.facility_id)) return false;
      
      // KPI Assignment-based filtering: check if user has access to this facility for this scope
      if (kpiAllowedFacilityIds !== null && !kpiAllowedFacilityIds.includes(e.facility_id)) {
        return false;
      }
      
      // Filter out biogenic records with biogenic_scope_selection='scope3' for orgs without scope3 access
      // This is a client-side backup in case the backend filter was bypassed
      if (!hasScope3Access && e.scope === 'biogenic' && e.biogenic_scope_selection === 'scope3') {
        return false;
      }
      
      if (e.scope !== activeScope) return false;
      if (filterFacility && e.facility_id !== filterFacility) return false;
      
      // Date range filter - handle different date formats with overlap logic
      if (filterDateRange.from || filterDateRange.to) {
        const period = e.reporting_period || '';
        
        // Helper to check if two date ranges overlap
        // Ranges overlap if: range1_start <= range2_end AND range1_end >= range2_start
        const rangesOverlap = (start1, end1, start2, end2) => {
          return start1 <= end2 && end1 >= start2;
        };
        
        // Get filter range as [year, month] tuples
        const filterStart = filterDateRange.from 
          ? [filterDateRange.from.getFullYear(), filterDateRange.from.getMonth() + 1]
          : [0, 1];
        const filterEnd = filterDateRange.to 
          ? [filterDateRange.to.getFullYear(), filterDateRange.to.getMonth() + 1]
          : [9999, 12];
        
        // Convert [year, month] to comparable number (YYYYMM)
        const toComparable = ([y, m]) => y * 100 + m;
        const filterStartNum = toComparable(filterStart);
        const filterEndNum = toComparable(filterEnd);
        
        // Handle FY format (e.g., "FY 2025-2026" = April 2025 to March 2026)
        if (period.startsWith('FY ')) {
          const fyMatch = period.match(/FY\s*(\d{4})/);
          if (fyMatch) {
            const fyStartYear = parseInt(fyMatch[1]);
            const fyEndYear = fyStartYear + 1;
            // FY runs from April of start year to March of end year
            const fyStartNum = toComparable([fyStartYear, 4]);  // April
            const fyEndNum = toComparable([fyEndYear, 3]);      // March
            if (!rangesOverlap(fyStartNum, fyEndNum, filterStartNum, filterEndNum)) {
              return false;
            }
          }
        }
        // Handle CY format (e.g., "CY2025" = January to December 2025)
        else if (period.startsWith('CY')) {
          const cyMatch = period.match(/^CY\s*(\d{4})$/);
          if (cyMatch) {
            const cyYear = parseInt(cyMatch[1]);
            // CY runs from January to December of that year
            const cyStartNum = toComparable([cyYear, 1]);   // January
            const cyEndNum = toComparable([cyYear, 12]);    // December
            if (!rangesOverlap(cyStartNum, cyEndNum, filterStartNum, filterEndNum)) {
              return false;
            }
          }
        }
        // Handle monthly format (e.g., "2025-04" or "2025-04 to 2025-06")
        else if (period.match(/^\d{4}-\d{2}/)) {
          const monthMatch = period.match(/^(\d{4})-(\d{2})/);
          if (monthMatch) {
            const monthYear = parseInt(monthMatch[1]);
            const monthNum = parseInt(monthMatch[2]);
            const periodNum = toComparable([monthYear, monthNum]);
            // Monthly entry is a single point, check if it falls within filter range
            if (periodNum < filterStartNum || periodNum > filterEndNum) {
              return false;
            }
          }
        }
        // Unknown format - exclude if filter is set
        else if (period) {
          // Try to parse as date
          const dateToCompare = new Date(period.split(' to ')[0] + '-01');
          if (!isNaN(dateToCompare.getTime())) {
            if (filterDateRange.from && dateToCompare < filterDateRange.from) return false;
            if (filterDateRange.to && dateToCompare > filterDateRange.to) return false;
          }
        }
      }
      
      if (filterCategory && e.category !== filterCategory) return false;
      
      // Calculation method filter (only for Scope 3)
      if (filterCalculationMethod && activeScope === 'scope3') {
        const emissionMethod = e.calculation_method_scope3 || '';
        if (filterCalculationMethod !== emissionMethod) return false;
      }
      
      // Frequency filter (monthly vs yearly)
      if (filterFrequency) {
        const emissionFrequency = e.frequency_type || 'monthly'; // Legacy records default to monthly
        if (filterFrequency !== emissionFrequency) return false;
      }
      
      // Search query filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const facilityName = facilities.find(f => f.id === e.facility_id)?.name?.toLowerCase() || '';
        const categoryName = e.category?.toLowerCase() || '';
        const fuelType = (e.sub_category || e.fuel_type || '')?.toLowerCase() || '';
        const activity = e.scope3_activity?.toLowerCase() || '';
        const reportingPeriod = e.reporting_period?.toLowerCase() || '';
        
        // Search in facility name, category, fuel/activity type, reporting period
        const matchesSearch = facilityName.includes(query) || 
                              categoryName.includes(query) || 
                              fuelType.includes(query) ||
                              activity.includes(query) ||
                              reportingPeriod.includes(query);
        if (!matchesSearch) return false;
      }
      
      return true;
    });
    
    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'date':
          // Sort by reporting period start date
          const dateA = new Date((a.reporting_period || '').split(' to ')[0] + '-01');
          const dateB = new Date((b.reporting_period || '').split(' to ')[0] + '-01');
          comparison = dateA - dateB;
          break;
        case 'created_at':
          // Sort by created_at timestamp
          const createdA = new Date(a.created_at || 0);
          const createdB = new Date(b.created_at || 0);
          comparison = createdA - createdB;
          break;
        case 'updated_at':
          // Sort by updated_at timestamp (or created_at if not available)
          const updatedA = new Date(a.updated_at || a.created_at || 0);
          const updatedB = new Date(b.updated_at || b.created_at || 0);
          comparison = updatedA - updatedB;
          break;
        case 'facility':
          // Sort by facility name
          const facilityA = facilities.find(f => f.id === a.facility_id)?.name || '';
          const facilityB = facilities.find(f => f.id === b.facility_id)?.name || '';
          comparison = facilityA.localeCompare(facilityB);
          break;
        case 'fuel':
          // Sort by fuel type/sub_category
          comparison = (a.sub_category || a.fuel_type || '').localeCompare(b.sub_category || b.fuel_type || '');
          break;
        case 'emissions':
          // Sort by total CO2e emissions
          comparison = (a.calculated_co2e || 0) - (b.calculated_co2e || 0);
          break;
        default:
          comparison = 0;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  }, [emissions, isSupplierUser, activeScope, filterFacility, filterCategory, filterFrequency, filterCalculationMethod, filterDateRange, activeFacilityIds, sortBy, sortOrder, facilities, searchQuery, hasScope3Access]);

  const uniqueCategories = useMemo(() => {
    return [...new Set(emissions.filter(e => e.scope === activeScope).map(e => e.category))];
  }, [emissions, activeScope]);

  // Get unique calculation methods for Scope 3 emissions (for filter dropdown)
  const uniqueCalculationMethods = useMemo(() => {
    if (activeScope !== 'scope3') return [];
    const methods = emissions
      .filter(e => e.scope === 'scope3' && e.calculation_method_scope3)
      .map(e => e.calculation_method_scope3);
    const unique = [...new Set(methods)].sort();

    return unique.map(method => ({
      value: method,
      label: configLabels.calculation_methods?.[method] || method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), 
    }));
    // return [...new Set(methods)].sort();
  }, [emissions, activeScope, configLabels.calculation_methods]);

  // Check if user is regular user (not admin or super_admin)
  const isRegularUser = user?.role === 'user';

  // E2: handleViewEvidence + handleDownloadEvidence — moved to useEvidenceManagement hook.

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (coreDataLoadError) {
    return <main className="mx-auto max-w-4xl px-6 py-16" data-testid="emissions-core-data-error-page">
      <LoadErrorState
        title="Unable to load emission data"
        message={coreDataLoadError}
        onRetry={fetchData}
        testId="emissions-core-data-load-error"
      />
    </main>;
  }

  // Check if organization has emission access
  // If enabled_access is null/undefined, default to scope1_2. If it's an empty array, no access.
  const enabledAccess = organization?.enabled_access;
  const hasEmissionAccess = enabledAccess === null || enabledAccess === undefined 
    ? true  // Default access if not set
    : enabledAccess.some(access => ['scope1_2', 'scope1_2_3'].includes(access));

  // hasScope3Access is computed earlier in the component (via useMemo) for use in filteredEmissions

  return (
    <div className="space-y-7" data-testid="emissions-page">
      <ModulePageHeader
        title="GHG Emissions"
        icon={Cloud}
        iconClassName="border-sky-200 bg-sky-50 text-sky-700"
        testId="ghg-emissions"
        aside={<div className="flex gap-3 items-center">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="text"
              placeholder="Search emissions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 w-64 bg-stone-50 border border-stone-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              data-testid="emissions-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button
            onClick={() => setShowFilters(!showFilters)}
            variant="outline"
            className={`rounded-full border-stone-300 bg-white text-stone-800 shadow-sm transition-[background-color,border-color,color] hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-900 ${showFilters ? 'border-emerald-700 bg-emerald-800 text-white hover:bg-emerald-900 hover:text-white' : ''}`}
            data-testid="emissions-toggle-filters-button"
          >
            <Filter className="w-4 h-4 mr-2" />
            {showFilters ? 'Hide' : 'Show'} Filters
          </Button>
          {hasEmissionAccess ? (
            <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
              <Button
                type="button"
                onClick={openCreateDialog}
                disabled={!canAddSupplierEmission}
                className="bg-primary hover:bg-primary/90 text-white rounded-full px-6"
                data-testid="add-emission-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Emission
              </Button>
              <DialogContent 
                key={editingEmission?.id || 'new'}
                className={`max-h-[90vh] overflow-x-hidden overflow-y-auto ${!editingEmission ? '!gap-2' : ''}`}
                style={{ width: 'calc(100vw - 2rem)', maxWidth: '72rem' }}
                onInteractOutside={handleInteractOutside}
                onEscapeKeyDown={handleEscapeKeyDown}
                onKeyDownCapture={(event) => {
                  if (event.key === '+' && event.target instanceof HTMLInputElement && event.target.type === 'number') event.preventDefault();
                }}
                onPasteCapture={(event) => {
                  if (event.target instanceof HTMLInputElement && event.target.type === 'number' && event.clipboardData?.getData('text')?.includes('+')) event.preventDefault();
                }}
                hideCloseButton={true}
              >
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <DialogTitle>{editingEmission ? 'Update' : 'Add'} Emission Record</DialogTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDialogChange(false)}
                      className="h-8 w-8 p-0 rounded-sm opacity-70 hover:opacity-100"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <DialogDescription className="sr-only" data-testid="emission-dialog-description">
                    Add or update an emission record with reporting and activity data.
                  </DialogDescription>
                </DialogHeader>
              {!editingEmission ? (
                <EmissionEntryForm
                  facilities={facilities}
                  fuelDatabase={fuelDatabase}
                  centralizedUnits={centralizedUnits}
                  formulaDefinitions={formulaDefinitions}
                  formulaParameters={formulaParameters}
                  emissionConfigurations={emissionConfigurations}
                  gwpConfig={gwpConfig}
                  dynamicScopes={visibleScopes}
                  dynamicCategories={dynamicCategories}
                  hasScope3Access={hasScope3Access}
                  getAuthHeader={getAuthHeader}
                  configLabels={configLabels}
                  organization={organization}
                  organizationGhgOverrides={effectiveSupplierGhgOverrides}
                  supplierGhgConfig={isSupplierUser ? supplierReportingConfig : null}
                  supplierContext={isSupplierUser}
                  assignedReportingPeriod={supplierReportingConfig}
                  onFormChange={markFormDirty}
                  kpiAccessInfo={kpiAccessInfo}
                  kpiCanAccessScope={kpiCanAccessScope}
                  kpiCanAccessPeriod={kpiCanAccessPeriod}
                  kpiGetPeriodRestrictions={kpiGetPeriodRestrictions}
                  kpiAllowedScopes={kpiAllowedScopes}
                  kpiPeriodRestrictions={kpiPeriodRestrictions}
                  filterFacilitiesByScope={filterFacilitiesByScope}
                  hasFullKPIAccess={hasFullKPIAccess}
                  ocrPrefillData={ocrPrefillData}
                  onSuccess={async () => {
                    // Note: OCR finalize-import is now handled in useEmissionSubmit hook
                    // which captures the actual emission_record_ids and calls finalize-import
                    console.log('[OCR Debug] onSuccess called, ocrPrefillData:', ocrPrefillData);
                    
                    setDialogOpen(false);
                    setIsFormDirty(false);
                    setOcrPrefillData(null);
                    fetchData();
                  }}
                  onCancel={() => {
                    setOcrPrefillData(null);
                    handleDialogChange(false);
                  }}
                />
              ) : (
                <EmissionEditForm
                  // Shared genuine form values. UI/loading state stays page-owned.
                  draft={editDraft}
                  onDraftChange={setEditDraft}
                  editingEmission={editingEmission}
                  useHistoricalEditValues={isOriginalEditSelection}
                  activitySearchTerm={activitySearchTerm}
                  loadingScope3EF={loadingScope3EF}
                  loadingBiogenicCategories={loadingBiogenicCategories}
                  isCalculatingEditEmployee={isCalculatingEditEmployee}
                  isEditLoading={isEditLoading}
                  editFormConfigLoading={editFormConfigLoading}
                  dynamicInputFields={dynamicInputFields}
                  effectiveCalculatedEmissions={effectiveCalculatedEmissions}
                  liveCalculationValidationError={liveCalculationValidationError || calculationError}
                  isCalculating={isCalculating}
                  isSaving={isSaving}
                  onC8AllocationMethodChange={handleC8AllocationMethodChange}
                  setActivitySearchTerm={setActivitySearchTerm}
                  // ---------- core data ----------
                  facilities={facilities}
                  dynamicScopes={visibleScopes}
                  hasScope3Access={hasScope3Access}
                  centralizedUnits={centralizedUnits}
                  fuelDatabase={fuelDatabase}
                  // ---------- KPI access control ----------
                  kpiAccessInfo={kpiAccessInfo}
                  kpiCanAccessScope={kpiCanAccessScope}
                  kpiCanAccessPeriod={kpiCanAccessPeriod}
                  kpiGetPeriodRestrictions={kpiGetPeriodRestrictions}
                  kpiAllowedScopes={kpiAllowedScopes}
                  kpiPeriodRestrictions={kpiPeriodRestrictions}
                  filterFacilitiesByScope={filterFacilitiesByScope}
                  hasFullKPIAccess={hasFullKPIAccess}
                  // ---------- computed/derived ----------
                  selectedFuel={selectedFuel}
                  isEditC7EmployeeCommuting={isEditC7EmployeeCommuting}
                  editActiveMonths={editActiveMonths}
                  ModuleDynamicFieldsRenderer={ModuleDynamicFieldsRenderer}
                  getCategoriesForScope={getCategoriesForScope}
                  getFuelsForCategory={getFuelsForCategory}
                  availableScope3Methods={availableScope3Methods}
                  availableScope3ActivityTypes={availableScope3ActivityTypes}
                  capabilities={editCapabilities}
                  fieldOptions={editGhgFieldOptions}
                  requiresSubcategory={requiresSubcategory}
                  availableSubcategories={availableSubcategories}
                  scope3EFData={scope3EFData}
                  filteredScope3Activities={filteredScope3Activities}
                  availableQuantityUnits={availableQuantityUnits}
                  // ---------- handlers ----------
                  handleSubmit={handleSubmit}
                  handleFuelSelect={handleFuelSelect}
                  handleCategorySelect={handleCategorySelect}
                  handleScopeChange={handleEditScopeChange}
                  handleBiogenicScopeChange={handleEditBiogenicScopeChange}
                  handleScope3MethodChange={handleEditScope3MethodChange}
                  markFormDirty={markFormDirty}
                  updateDynamicFieldValue={updateDynamicFieldValue}
                  getMethodLabel={getMethodLabel}
                  handleCalculateEditEmployeeMonth={handleCalculateEditEmployeeMonth}
                  handleFileUpload={handleFileUpload}
                  handleRemoveEvidence={handleRemoveEvidence}
                  handleDeleteExistingEvidence={handleDeleteExistingEvidence}
                  handleDeleteAllEvidences={handleDeleteAllEvidences}
                  handleC7EvidenceUpload={handleC7EditEvidenceUpload}
                  handleC7EvidenceRemove={handleC7EditEvidenceRemove}
                  handleC7EvidenceDownload={handleDownloadEvidence}
                  handleDialogChange={handleDialogChange}
                  assignedReportingPeriod={supplierReportingConfig}
                />
              )}
            </DialogContent>
          </Dialog>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button 
                      className="bg-stone-300 text-stone-500 rounded-full px-6 cursor-not-allowed" 
                      disabled
                      data-testid="add-emission-button-disabled"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Emission
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Your organization does not have emission access. Contact your administrator.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>}
      />

      {showFilters && (
        <EmissionFilters
          filterFacility={filterFacility}
          setFilterFacility={setFilterFacility}
          filterCategory={filterCategory}
          setFilterCategory={setFilterCategory}
          filterFrequency={filterFrequency}
          setFilterFrequency={setFilterFrequency}
          filterCalculationMethod={filterCalculationMethod}
          setFilterCalculationMethod={setFilterCalculationMethod}
          filterDateRange={filterDateRange}
          setFilterDateRange={setFilterDateRange}
          facilities={facilities}
          uniqueCategories={uniqueCategories}
          uniqueCalculationMethods={uniqueCalculationMethods}
          isScope3={activeScope === 'scope3'}
        />
      )}

      <Tabs value={activeScope} onValueChange={(value) => { 
        // Only allow scope3 tab if org has access
        const isScope3 = value === 'scope3';
        if (isScope3 && !hasScope3Access) return;
        setActiveScope(value);
        // Keep the URL in sync when used inside the GHG workspace.
        if (location.pathname.startsWith('/ghg/')) {
          navigate(`/ghg/${value}`, { replace: true });
        }
        // Reset category filter when changing scopes to prevent showing no emissions
        setFilterCategory('');
        // Reset calculation method filter when leaving Scope 3
        if (value !== 'scope3') {
          setFilterCalculationMethod('');
        }
        // Reset biogenic state when changing tabs
        if (value !== 'biogenic') {
          setBiogenicScopeSelection('');
        }
      }} className="w-full">
        <div className="flex flex-wrap items-center gap-3">
        <TabsList className="inline-grid w-fit min-w-0 gap-1 rounded-full border border-emerald-950/15 bg-white p-1 shadow-sm" style={{ gridTemplateColumns: `repeat(${Math.max(visibleScopes.length, 1)}, minmax(78px, 112px))` }}>
          {visibleScopes.map(s => {
            const isScope3 = s.code === 'scope3';
            // Check both organization-level and KPI assignment-level access
            const orgDisabled = isScope3 && !hasScope3Access;
            const kpiDisabled = !canAccessScopeWithKPI(s.code);
            const isDisabled = orgDisabled || kpiDisabled;
            const disabledReason = orgDisabled ? 'Not Available' : kpiDisabled ? 'Not Assigned' : '';
            return (
              <TabsTrigger
                key={s.code}
                value={s.code}
                disabled={isDisabled}
                className={`relative overflow-hidden rounded-full px-4 transition-colors duration-200 ${isDisabled ? 'cursor-not-allowed opacity-60 text-stone-400' : 'text-stone-600 hover:text-emerald-900 data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:shadow-none'}`}
                data-testid={`scope-tab-${s.code}`}
              >
                {activeScope === s.code && !isDisabled && <motion.span layoutId="ghg-scope-active-indicator" className="pointer-events-none absolute inset-0 rounded-full bg-emerald-800 shadow-sm" transition={{ type: 'spring', stiffness: 420, damping: 34, mass: 0.55 }} aria-hidden="true" />}
                <span className="relative z-10">{s.name}</span>
                {isDisabled && disabledReason && (
                  <span className="absolute -top-2 -right-2 z-20 px-1.5 py-0.5 bg-stone-200 text-stone-600 text-[9px] font-semibold rounded whitespace-nowrap">
                    {disabledReason}
                  </span>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>
        {hasCustomizedColumnWidths && <Button type="button" variant="ghost" size="sm" onClick={() => { emissionDataGridRef.current?.resetColumnWidths(); setHasCustomizedColumnWidths(false); }} className="ml-auto h-9 shrink-0 gap-1.5 text-xs text-stone-600 hover:bg-emerald-50 hover:text-emerald-700" data-testid="emissions-reset-column-widths-button">
          <RotateCcw className="h-3.5 w-3.5" />Reset widths
        </Button>}
        </div>

        <TabsContent value={activeScope} className="mt-6">
          <div key={activeScope} className="animate-in fade-in-0 slide-in-from-bottom-1 duration-200 motion-reduce:animate-none" data-testid={`emissions-scope-content-${activeScope}`}>
            {/* Enterprise Data Grid Layout */}
            <EmissionDataGrid
              ref={emissionDataGridRef}
              activeScope={activeScope}
              onColumnWidthsCustomizedChange={setHasCustomizedColumnWidths}
              filteredEmissions={filteredEmissions}
              facilities={facilities}
              filteredScope3Activities={filteredScope3Activities}
              getMethodLabel={getMethodLabel}
              isRegularUser={isRegularUser}
              approvalWorkflowEnabled={Boolean(organization?.approval_workflow_enabled)}
              hideHistoryActions={isSupplierUser}
              lockedEmissionIds={lockedSupplierEmissionIds}
              handleEdit={handleEdit}
              fetchHistory={fetchHistory}
              openDeleteConfirm={openDeleteConfirm}
              onBulkDelete={openBulkDeleteConfirm}
              showFilters={showFilters}
              filterFacility={filterFacility}
              filterDateRange={filterDateRange}
              filterCategory={filterCategory}
              filterFrequency={filterFrequency}
            />
          </div>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Emission Record</AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div>
                      <span>Are you sure you want to delete this emission record? This action cannot be undone.</span>
                      {emissionToDelete && (
                        <div className="mt-2 p-2 bg-stone-50 rounded text-sm">
                          <strong>Facility:</strong> {facilities.find(f => f.id === emissionToDelete.facility_id)?.name || 'Unknown'}<br/>
                          <strong>Category:</strong> {emissionToDelete.category}<br/>
                          <strong>Quantity:</strong> {formatEmissionQuantity(emissionToDelete)}
                        </div>
                      )}
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel onClick={() => setEmissionToDelete(null)}>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={() => emissionToDelete && handleDelete(emissionToDelete.id)}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            
            {/* Bulk Delete Confirmation Dialog */}
            <AlertDialog open={bulkDeleteConfirmOpen} onOpenChange={setBulkDeleteConfirmOpen}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Multiple Emission Records</AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div>
                      <span>Are you sure you want to delete <strong>{bulkDeleteIds.length}</strong> emission record(s)? This action cannot be undone.</span>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel onClick={() => setBulkDeleteConfirmOpen(false)}>
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleBulkDelete}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Delete {bulkDeleteIds.length} Record(s)
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
        </TabsContent>
      </Tabs>

      {/* Version History Dialog - With field-level changes */}
      {!isRegularUser && (
        <EmissionHistoryDialog
          open={historyDialogOpen}
          onOpenChange={setHistoryDialogOpen}
          history={selectedEmissionHistory}
          facilities={facilities}
        />
      )}
      
      {/* Unsaved Changes Confirmation Dialog (#19) */}
      <AlertDialog open={showUnsavedChangesDialog} onOpenChange={setShowUnsavedChangesDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to discard them?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleContinueEditing}>
              Continue Editing
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDiscardChanges}
              className="bg-red-600 hover:bg-red-700"
            >
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
